
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { isTrainingMode } from '../training/trainingMode';
import { resetTrainingData } from '../training/trainingBackend';
import { setPageContext, clearPageContext } from '../agent/pageContext';
import { usePanelUrlSync, findVehicleByParam } from '../hooks/usePanelUrlSync';
import { useAuth } from '../components/AuthContext';
import { useChangeManager } from '../context/ChangeManagerContext';
import dayjs from 'dayjs';
import {
  Box,
  Grid,
  CircularProgress,
  TextField,
  useMediaQuery,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormControlLabel,
  Switch,
  InputAdornment,
  Menu,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import ReservationTable, { TURNAROUND_COLOR, TURNAROUND_SAME_TIME_COLOR } from '../components/ReservationTable';
import VehicleDetailPanel from '../components/VehicleDetailPanel';
import { updateVehicle } from '../api/vehicleService';
import { sendLineWorksNotify } from '../api/lineworksNotify';
import { validateReservations } from '../schemas/dataSchemas';
import { useLineChat } from '../hooks/useLineChat';
import { useLineUsersForReservations } from '../hooks/useLineUsersForReservations';
import {
  fetchDispatchReservations,
  fetchStaffList,
  saveDispatchReservation,
  saveReservationStaff,
  toggleReservationLicense,
  updateReservationCommunicationTools,
  mergeStaffPriorities,
  onStaffPriorityChange,
} from '../api/dispatchService';
import { useSquarePaymentData } from '../hooks/useSquarePaymentData';
import { reservationSyncService } from '../services/reservationSyncService';
import { vehicleSyncService } from '../services/vehicleSyncService';
import { useReservationData } from '../context/ReservationDataContext';
import { isOnDuty, isSystemAccount } from '../utils/staffPriority';
import { isSquarePaid, invoiceStatusChip, computeSquareAmounts } from '../utils/paymentStatus';
import { SYNC_PAST_DAYS, SYNC_FUTURE_DAYS } from '../constants/calendar';

import { CAR_TYPES, ensureCarTypesLoaded } from '../data/carTypes';
import { useReservationStatuses } from '../hooks/useReservationStatuses';
import { useStatusMaster } from '../hooks/useStatusMaster';
import { useDateTags } from '../hooks/useDateTags';
import { useBottomTabs } from '../hooks/useBottomTabs';
import { useVehiclePanel } from '../hooks/useVehiclePanel';
import { useStatusDialogs } from '../hooks/useStatusDialogs';
import VehicleAssignModal from '../components/modals/VehicleAssignModal';
import LineChatPanel from '../components/LineChatPanel';
import ReservationChatPanel from '../components/ReservationChatPanel';
import { useReservationChat } from '../hooks/useReservationChat';
import { reservationChatPusher } from '../services/reservationChatPusher';
import { isReservationChatEnabled } from '../config/backend';
import { toReservationChatInstance, isReservationSessionKey } from '../lib/reservationChat';
import { toAnonChatInstance } from '../lib/lpChatBoard';
import VehiclePositionMap, { PICKUP_SUB_ZONES, RETURN_SUB_ZONES } from '../components/VehiclePositionMap';
import DigestWindow from '../components/DigestWindow';
import LiveWindow from '../components/LiveWindow';
import { getBackendApiOverride, getInstance, isLiveReportEnabled } from '../config/backend';
import { getArrivalContact, needsShuttleDriver, AIRPORT_CALLED } from '../lib/arrivalContact';
import { dropStaleAltShuttleKey } from '../lib/transportVehicle';
import { UI } from '../styles/uiTokens';
import {
  fetchNahaFlights,
  parseReservationFlight,
  findArrival,
  fetchGeminiFlightFixes,
} from '../services/nahaFlightService';
import { fetchMiyakoFlights } from '../services/miyakoFlightService';

// ドメイン別 backend 上書き（pit-booking.* → miyako backend）を最優先で適用
const API_BASE = getBackendApiOverride() || process.env.REACT_APP_API_URL || 'https://backend.n-car.info/api';

// ページタイトル設定（コンポーネント内で呼ばれる）
const PAGE_TITLE = '貸出返却リスト';

// Square Invoicesの対象ステータス（予約確定=1, 貸出済み=3, 貸出中=2, 要連絡/未確定=6）
const SQUARE_TARGET_STATUSES = [1, 2, 3, 4, 6];

// デフォルトのステータス（APIから取得できない場合のフォールバック）

const DEFAULT_RESERVATION_STATUS_LIST = [];

export default function DayListReservations() {
  // エージェントに現在ページを伝える（選択概念が無いのでページのみ。ai-control-center.md §15）
  useEffect(() => {
    setPageContext({ page: 'day_list' });
    return () => clearPageContext('day_list');
  }, []);

  const { token, user } = useAuth();
  const { recordChange } = useChangeManager();
  const isMobile = useMediaQuery('(max-width:800px)');
  const [tabValue, setTabValue] = useState(0);
  const [mobileSearchTerm, setMobileSearchTerm] = useState('');

  // LINE Chat integration for linking indicators
  const { users: lineUsers, fetchUsers: fetchLineUsers, sendMessage } = useLineChat();

  // ページタイトル設定
  useEffect(() => {
    document.title = PAGE_TITLE;
    fetchLineUsers(); // Initial fetch for LINE status indicators
  }, [fetchLineUsers]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [hoveredVehicleId, setHoveredVehicleId] = useState(null);

  // LINE Chat modal state
  const [lineChatModal, setLineChatModal] = useState({ open: false, lineUser: null, reservationId: null });

  // Initialize date from URL query parameter 'date' or default to today
  const initialDate = searchParams.get('date') || dayjs().format('YYYY-MM-DD');
  const [selectedDate, setSelectedDate] = useState(initialDate);

  const { vehicles: contextVehicles, reservations: contextReservations } = useReservationData();

  const [departures, setDepartures] = useState([]);
  const [returns, setReturns] = useState([]);
  const [vehicles, setVehicles] = useState({});
  const [loading, setLoading] = useState(true);

  // 車両DnDスワップ
  const [vehicleDragSource, setVehicleDragSource] = useState(null);
  const [vehicleSwapConfirm, setVehicleSwapConfirm] = useState(null); // { source, target }

  // Context から vehicles を反映
  useEffect(() => {
    if (Object.keys(contextVehicles).length > 0) {
      setVehicles(contextVehicles);
    }
  }, [contextVehicles]);

  // Context の予約データ変更時に departures/returns を再フィルタ（Pusher 即時反映）
  useEffect(() => {
    if (contextReservations.length > 0 && selectedDate) {
      const filteredDep = contextReservations.filter(r => r.start_date && r.start_date.substring(0, 10) === selectedDate);
      filteredDep.sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));
      const filteredRet = contextReservations.filter(r => r.end_date && r.end_date.substring(0, 10) === selectedDate);
      filteredRet.sort((a, b) => (a.end_date || '').localeCompare(b.end_date || ''));
      setDepartures(filteredDep);
      setReturns(filteredRet);
      setLoading(false);
      // 最終更新時刻を更新
      const now = new Date();
      setLastUpdated?.(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);
    }
  }, [contextReservations, selectedDate]);

  const [refreshKey, setRefreshKey] = useState(0);
  // モバイル設定パネル用のカラム表示設定
  const MOBILE_FIELD_OPTIONS = [
    { id: 'name', label: '名前' },
    { id: 'vehicle', label: '車両' },
    { id: 'time', label: '時間' },
    { id: 'status', label: 'ステータス' },
    { id: 'pax', label: '人数' },
    { id: 'flight', label: 'ARR/DEP' },
    { id: 'license', label: '免許' },
    { id: 'payment', label: '支払' },
    { id: 'contact', label: '連絡手段' },
  ] as const;

  const [mobileFieldVisibility, setMobileFieldVisibility] = useState<Record<string, boolean>>(() => {
    const defaults = MOBILE_FIELD_OPTIONS.reduce((acc, f) => ({ ...acc, [f.id]: true }), {} as Record<string, boolean>);
    try {
      const saved = localStorage.getItem('reservation_table_visible_departure');
      if (saved) return { ...defaults, ...JSON.parse(saved) };
    } catch {}
    return defaults;
  });
  const [settingsVersion, setSettingsVersion] = useState(0);

  // モバイル設定パネル用の予約ステータス絞り込み
  const [mobileStatusFilter, setMobileStatusFilter] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('reservation_table_status_departure');
      if (saved) return JSON.parse(saved);
    } catch {}
    return {};
  });

  const handleMobileFieldToggle = (fieldId: string) => {
    setMobileFieldVisibility(prev => {
      const next = { ...prev, [fieldId]: !prev[fieldId] };
      // 両方のテーブル（departure/return）に同じ設定を適用
      localStorage.setItem('reservation_table_visible_departure', JSON.stringify(next));
      localStorage.setItem('reservation_table_visible_return', JSON.stringify(next));
      return next;
    });
    // ReservationTable を再マウントして設定を反映
    setSettingsVersion(v => v + 1);
  };

  const handleMobileStatusToggle = (statusId: string | number) => {
    setMobileStatusFilter(prev => {
      const currentState = prev[String(statusId)] !== false; // 未定義はデフォルト ON
      const next = { ...prev, [String(statusId)]: !currentState };
      // 両方のテーブル（departure/return）に同じ設定を適用
      localStorage.setItem('reservation_table_status_departure', JSON.stringify(next));
      localStorage.setItem('reservation_table_status_return', JSON.stringify(next));
      return next;
    });
    setSettingsVersion(v => v + 1);
  };

  const [compactMode, setCompactMode] = useState(() => {
    try {
      const raw = document.cookie.split('; ').find(c => c.startsWith('daylist_compact='));
      return raw ? raw.split('=')[1] === 'true' : false;
    } catch { return false; }
  });

  // 貸出・返却の統合リスト表示（時間順1リスト + 30分グリッド + ターンアラウンド連結）
  const [mergedView, setMergedView] = useState(() => {
    try {
      const raw = document.cookie.split('; ').find(c => c.startsWith('daylist_merged='));
      return raw ? raw.split('=')[1] === 'true' : false;
    } catch { return false; }
  });

  // 最終同期時刻ラベル（カレンダーページと同じ方式: reservationSyncService の
  // lastSyncTime を10秒ごとに HH:MM 表示。統合ボタンの左に表示する）
  const [lastSyncLabel, setLastSyncLabel] = useState('');
  useEffect(() => {
    const fmt = () => {
      const ts = reservationSyncService.getStatus().lastSyncTime;
      if (!ts) { setLastSyncLabel(''); return; }
      const d = new Date(ts);
      if (isNaN(d.getTime())) { setLastSyncLabel(''); return; }
      setLastSyncLabel(`${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`);
    };
    fmt();
    const id = setInterval(fmt, 10000); // 10秒ごとに表示更新
    return () => clearInterval(id);
  }, []);

  // 貸出返却リストの「⬇」→ 車両ポジションの該当カードへジャンプ（2026-08-10）
  //   セクションが画面上部に来るまでスクロールし、対象カードを一定時間グローさせる。
  //   同じ車両を続けて押しても再発火するよう nonce を持つ。
  const vehiclePositionRef = useRef(null);
  const [locateVehicle, setLocateVehicle] = useState({ id: null, nonce: 0 });
  const handleLocateVehicle = useCallback((vehicleId) => {
    if (vehicleId == null) return;
    const section = vehiclePositionRef.current;
    if (section) {
      // ⚠️ このページのスクロール主体は window ではなく App.tsx の <main>（overflow:auto）。
      //    window.scrollTo では何も起きないため、実際にスクロールする祖先要素を探して動かす。
      let scroller = section.parentElement;
      while (scroller) {
        const oy = window.getComputedStyle(scroller).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && scroller.scrollHeight > scroller.clientHeight) break;
        scroller = scroller.parentElement;
      }
      // sticky ヘッダー（日付/ダイジェスト行）に隠れないよう、その高さ分だけ手前で止める
      const sticky = document.querySelector('[data-daylist-sticky]');
      const offset = (sticky ? sticky.getBoundingClientRect().height : 0) + 8;
      if (scroller) {
        const delta = section.getBoundingClientRect().top - scroller.getBoundingClientRect().top - offset;
        scroller.scrollTo({ top: Math.max(0, scroller.scrollTop + delta), behavior: 'smooth' });
      } else {
        const top = section.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      }
    }
    setLocateVehicle(prev => ({ id: String(vehicleId), nonce: prev.nonce + 1 }));
  }, []);

  // 車両ポジションから「予約詳細」を開く要求（貸出/返却リストのサイドパネルを再利用）
  const [openResRequest, setOpenResRequest] = useState({ id: null, nonce: 0 });
  const handleOpenReservationPanel = useCallback((r) => {
    if (!r?.id) return;
    setOpenResRequest(prev => ({ id: String(r.id), nonce: prev.nonce + 1 }));
  }, []);

  // ステータスマスター（共通フック経由）
  const { vehicleStatusList, locationStatusList, reservationStatusList } = useStatusMaster({ locationNullLabel: '-' });

  // ステータスモーダル・確認ダイアログ・トースト（フック経由）
  const {
    statusModal, setStatusModal, locationStatusModal, setLocationStatusModal, confirmDialog,
    moveTargetModal, setMoveTargetModal, handleMoveTargetSelect,
    toast, setToast, handleCloseToast,
    executeStatusUpdate,
    handleStatusClick, handleStatusUpdate, handleConfirmClose,
    handleLocationStatusClick, handleLocationStatusUpdate,
  } = useStatusDialogs({
    user, vehicleStatusList, locationStatusList, setVehicles, setDepartures, setReturns,
    // 迎え中/送り中 に変えた直後に運転者を聞く（車両ポジションの DnD と同じ体験。2026-08-25）
    onRequestDriver: (reservation: any, listType: any) => handleStaffClick(reservation, 'driver', listType),
  }) as any;

  // 車両パネル（フック経由）
  const {
    selectedVehicle, setSelectedVehicle,
    selectedVehicleReservation, setSelectedVehicleReservation,
    vehiclePanelDirty, setVehiclePanelDirty,
    unsavedConfirm,
    handleBeforeOpenReservation, handleVehicleClick, handleClosePanel,
    handleUnsavedConfirmAction, handleSaveVehicle,
  } = useVehiclePanel({ user, setVehicles, onToast: setToast });

  // ---- 車両パネル URL 同期（?vehicle=）----
  // 開閉は useVehiclePanel の既存ハンドラ経由（未保存確認ダイアログの挙動を維持）。
  // 仕様: feature-sidepanel.md「URL連動」
  usePanelUrlSync<any>({
    paramKey: 'vehicle',
    current: selectedVehicle,
    getUrlValue: (v) => String(v?.id ?? '') || null,
    resolve: (val) => findVehicleByParam(Object.values(vehicles) as any[], val),
    isReady: Object.keys(vehicles).length > 0,
    onOpen: (v) => handleVehicleClick(v),
    onClose: () => handleClosePanel(),
    onNotFound: (v) => setToast({ open: true, message: `車両 ${v} が見つかりません`, severity: 'error' }),
  });
  const [upcomingStatuses, setUpcomingStatuses] = useState({}); // vehicle_id -> { type: 1..4, color: '...', label: '...' }
  // 連絡モーダル: { reservation, rowType }（貸出行と返却行で内容が変わる。2026-08-24）
  const [contactModalReservation, setContactModalReservation] = useState<any>(null);
  const [allFutureReservations, setAllFutureReservations] = useState({});

  // LINEチャットモーダルを開くハンドラ
  const handleOpenLineChat = useCallback((lineUser, reservationId) => {
    setLineChatModal({ open: true, lineUser, reservationId: String(reservationId) });
  }, []);

  // 自社チャット（予約チャット）を予約行から開く（2026-10-30・booking-platform/md/customer-chat.md §13-7）。
  // 一覧は LINE ページと共有（useReservationChat）。この画面だけ開いているときもアイコンが出るよう、ここでも取得する
  // （LP の Pusher に繋がっていれば 5 分ごと・未接続なら 1 分ごと。新しい会話は Pusher のイベントで取り直される）
  const resvChat = useReservationChat();
  const resvChatEnabled = isReservationChatEnabled();
  const [resvChatSessionKey, setResvChatSessionKey] = useState<string | null>(null);
  useEffect(() => {
    if (!resvChatEnabled) return;
    reservationChatPusher.connect();
    resvChat.fetchSessions();
    const timer = setInterval(() => { resvChat.fetchSessions(); }, resvChat.realtimeConnected ? 300000 : 60000);
    return () => clearInterval(timer);
  }, [resvChatEnabled, resvChat.fetchSessions, resvChat.realtimeConnected]);
  const handleOpenResvChat = useCallback((sessionKey: string) => { setResvChatSessionKey(sessionKey); }, []);
  const handleCloseResvChat = useCallback(() => { setResvChatSessionKey(null); }, []);
  const resvChatInstance = useMemo(() => {
    if (!resvChatSessionKey) return null;
    const sess = resvChat.sessions.find((x) => x.session_key === resvChatSessionKey);
    if (!sess) return null;
    const all = reservationSyncService.getReservations();
    // 予約前の匿名の会話（予約チャットのパネルから辿る）は匿名チャットとして開く
    if (!isReservationSessionKey(sess.session_key)) return toAnonChatInstance(sess);
    return toReservationChatInstance(sess, (id) => all.find((r) => String(r.id) === String(id)), '', '');
  }, [resvChatSessionKey, resvChat.sessions]);

  const handleCloseLineChat = useCallback(() => {
    setLineChatModal({ open: false, lineUser: null, reservationId: null });
    fetchLineUsers(); // チャット閉じた後に未読数等を更新
  }, [fetchLineUsers]);

  // 車両アサインモーダル用state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignTargetReservation, setAssignTargetReservation] = useState(null);
  const [carTypesList, setCarTypesList] = useState([]);

  // 車種ロード
  useEffect(() => {
    ensureCarTypesLoaded().then(() => {
      setCarTypesList([...CAR_TYPES.list]);
    });
  }, []);

  // スタッフ選択モーダル state
  const [staffList, setStaffList] = useState([]);
  const [staffModal, setStaffModal] = useState({
    open: false,
    reservation: null,
    staffType: null,  // 'driver' or 'handler'
    listType: null    // 'departure' or 'return'
  });

  // Square決済データ（DS1: search_by_contact.php 経由）
  const { squarePaymentCache, squarePaymentLoading, fetchPaymentData, getPaymentData, setSquarePaymentCache, refreshSingleContact } = useSquarePaymentData(SQUARE_TARGET_STATUSES);

  const [squareModal, setSquareModal] = useState<{
    open: boolean;
    reservation: any;
    paymentData: any;
    loading: boolean;
  }>({
    open: false,
    reservation: null,
    paymentData: null,
    loading: false,
  });

  // 下部タブ（フック経由）
  const {
    bottomTabValue, setBottomTabValue,
    bottomTabConfigs,
    tabConfigDialogOpen, setTabConfigDialogOpen,
    editingTabConfigs,
    locationGroupVehicles,
    handleBottomTabChange, handleOpenTabConfig, handleSaveTabConfig, handleTabConfigChange,
  } = useBottomTabs({ vehicles, locationStatusList, vehicleStatusList, reservationStatusList, departures, returns, onToast: setToast });

  // 日付タグ（フック経由）
  const {
    dateTags,
    displayDateTags,
    addDatePickerValue, setAddDatePickerValue,
    handleAddDateTag, handleRemoveDateTag, handleDateTagClick,
  } = useDateTags({ onToast: setToast, onDateSelect: setSelectedDate });

  // スタッフ一覧を取得
  useEffect(() => {
    const loadStaffList = async () => {
      try {
        const staff = await fetchStaffList({ per_page: 100 });
        setStaffList(mergeStaffPriorities(staff));
      } catch (error) {
        console.error('Failed to fetch staff list:', error);
      }
    };
    loadStaffList();
  }, []);

  // スタッフ優先度の変更を監視（本日勤務モーダルでの更新を即座に反映）
  useEffect(() => {
    const cleanup = onStaffPriorityChange(() => {
      // localStorageの最新priorityでstaffListを再マージ
      setStaffList(prev => mergeStaffPriorities(prev));
    });
    return cleanup;
  }, []);

  // ピン留めスタッフ一覧（運転者選択用）
  const pinnedStaffUsers = useMemo(() => {
    if (!Array.isArray(lineUsers)) return [];
    return lineUsers
      .filter(user => {
        if (!user.is_pinned) return false;
        const rids = user.reservation_ids;
        if (!rids) return false;
        const idArray = Array.isArray(rids) ? rids : String(rids).split(',').map(s => s.trim());
        return idArray.some(rid => String(rid).startsWith('STAFF:'));
      })
      .map(user => {
        const rids = user.reservation_ids;
        const idArray = Array.isArray(rids) ? rids : String(rids).split(',').map(s => s.trim());
        const staffId = idArray.find(id => String(id).startsWith('STAFF:'));
        const staffName = staffId?.replace('STAFF:', '') || user.line_display_name;
        return { ...user, staffName };
      });
  }, [lineUsers]);

  // Sync state with URL if URL changes (e.g. browser back button)
  useEffect(() => {
    const dateParam = searchParams.get('date');
    if (dateParam && dateParam !== selectedDate) {
      setSelectedDate(dateParam);
    }
  }, [searchParams]);

  // selectedDate が SyncService のキャッシュ範囲内かチェック
  const isDateInSyncRange = useCallback((dateStr) => {
    const date = dayjs(dateStr);
    const today = dayjs();
    return date.isAfter(today.subtract(SYNC_PAST_DAYS + 1, 'day')) &&
      date.isBefore(today.add(SYNC_FUTURE_DAYS + 1, 'day'));
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;

      // キャッシュ範囲内であれば、reservationSyncService のキャッシュから絞り込む
      if (isDateInSyncRange(selectedDate)) {
        const cached = reservationSyncService.getReservations();
        if (cached.length > 0) {
          const filteredDep = cached.filter(r => r.start_date && r.start_date.substring(0, 10) === selectedDate);
          filteredDep.sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));
          const filteredRet = cached.filter(r => r.end_date && r.end_date.substring(0, 10) === selectedDate);
          filteredRet.sort((a, b) => (a.end_date || '').localeCompare(b.end_date || ''));
          setDepartures(filteredDep);
          setReturns(filteredRet);
          setLoading(false);
          fetchLineUsers();
          return;
        }
      }

      setLoading(true);
      try {
        // Fetch Rentals (Departures) (rental_date_from/to = selectedDate)
        // Use service to ensure mappers (like json_data parsing) are applied
        const rawDepData = await fetchDispatchReservations({
          rentalDateFrom: selectedDate,
          rentalDateTo: selectedDate,
          start_date: '2000-01-01',
          end_date: '2100-12-31'
        });
        const depData = validateReservations(rawDepData);

        // Client-side filter fallback in case API ignores rental_date params
        // Use substring to avoid timezone issues (API returns ISO with offset, we just want the date part)
        const filteredDep = Array.isArray(depData)
          ? depData.filter(r => r.start_date && r.start_date.substring(0, 10) === selectedDate)
          : [];
        filteredDep.sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));

        setDepartures(filteredDep);

        // Fetch Returns (return_date_from/to = selectedDate)
        const rawRetData = await fetchDispatchReservations({
          returnDateFrom: selectedDate,
          returnDateTo: selectedDate,
          start_date: '2000-01-01',
          end_date: '2100-12-31'
        });
        const retData = validateReservations(rawRetData);

        // Client-side filter fallback
        const filteredRet = Array.isArray(retData)
          ? retData.filter(r => r.end_date && r.end_date.substring(0, 10) === selectedDate)
          : [];

        // 時間の昇順で並び替え
        filteredRet.sort((a, b) => {
          const timeA = a.end_date || '';
          const timeB = b.end_date || '';
          return timeA.localeCompare(timeB);
        });

        setReturns(filteredRet);

        // Square決済データを非同期で取得（UIブロックしない、確認済みはスキップ）
        fetchPaymentData([...filteredDep, ...filteredRet]);

      } catch (error) {
        console.error('Failed to fetch day list data', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    fetchLineUsers(); // Also refresh LINE users
  }, [token, selectedDate, refreshKey, fetchLineUsers]);

  // 同期サービスによる自動更新
  useEffect(() => {
    const handleReservationChanges = (changes, allReservations) => {
      // 選択中の日付に該当する予約を抽出
      const filteredDep = allReservations.filter(r => r.start_date && r.start_date.substring(0, 10) === selectedDate);
      filteredDep.sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));
      const filteredRet = allReservations.filter(r => r.end_date && r.end_date.substring(0, 10) === selectedDate);
      filteredRet.sort((a, b) => (a.end_date || '').localeCompare(b.end_date || ''));

      setDepartures(filteredDep);
      setReturns(filteredRet);
      console.log(`[DayList] Sync updated: deps=${filteredDep.length}, rets=${filteredRet.length} (Total in sync cache: ${allReservations.length})`);
    };

    const unsubscribeRes = reservationSyncService.subscribe(handleReservationChanges);

    // 購読開始時に現在のキャッシュからも1回更新（同期を待たずに済むように）
    const currentRes = reservationSyncService.getReservations();
    if (currentRes.length > 0) {
      handleReservationChanges([], currentRes);
    }

    return () => {
      unsubscribeRes();
    };
  }, [selectedDate]);

  // 本日以降の予約状況を非同期で取得して「i」アイコンのステータスと車両位置の次回予約を設定
  useEffect(() => {
    const fetchUpcomingReservations = async () => {
      try {
        const baseDate = dayjs(selectedDate);
        const startDate = baseDate.format('YYYY-MM-DD');
        // 本日以降30日先まで取得し、次回予約を正確に表示する
        const endDate = baseDate.add(30, 'day').format('YYYY-MM-DD');

        // 30日以内の予約を全取得
        const rawData = await fetchDispatchReservations({
          start_date: `${startDate} 00:00:00`,
          end_date: `${endDate} 23:59:59`
        });
        const data = validateReservations(rawData);

        // 車両ごとの状況を解析
        const statusMap = {};
        const futureResMap = {};
        const date1Str = baseDate.add(1, 'day').format('YYYY-MM-DD');
        const date2Str = baseDate.add(2, 'day').format('YYYY-MM-DD');

        // 除外するステータス（オファー中、予約がいっぱい、予約に至らなかった、キャンセル済み）
        const excludeStatuses = ['cancelled', 'nonreserved', 'full', 'offer', 4, 5, 6, 7];

        // すべての車両について次回の予約を調査
        Object.values(vehicles).forEach((v: any) => {
          const vehicleId = v.id;
          if (!vehicleId) return;

          // この車両の本日以降の予約を抽出（本日当日分も含む）
          const futureRes = data.filter(r =>
            Number(r.vehicle_id) === Number(vehicleId) &&
            !dayjs(r.start_date).isBefore(baseDate.startOf('day')) &&
            !excludeStatuses.includes(r.status) &&
            !excludeStatuses.includes(Number(r.status))
          );

          if (futureRes.length === 0) {
            statusMap[vehicleId] = { type: 4, color: '#bdbdbd', label: '次回予約なし', next: null };
            return;
          }

          // 開始時間で昇順ソート
          futureRes.sort((a, b) => dayjs(a.start_date).unix() - dayjs(b.start_date).unix());

          const next = futureRes[0];
          const nextStartDate = dayjs(next.start_date).format('YYYY-MM-DD');

          let type = 4;
          let color = '#bdbdbd';
          let label = '次回予約なし';

          if (nextStartDate === startDate) {
            type = 1;
            color = '#d32f2f';
            label = `次${dayjs(next.start_date).format('HH:mm')}に出る`;
          } else if (nextStartDate === date1Str) {
            type = 2;
            color = '#1976d2';
            label = '翌日に予約あり';
          } else if (nextStartDate === date2Str) {
            type = 3;
            color = '#03a9f4';
            label = '翌々日に予約あり';
          } else {
            // 3日以上先の予約がある場合
            type = 5;
            color = '#9e9e9e';
            label = '3日以上先に予約あり';
          }

          statusMap[vehicleId] = {
            type,
            color,
            label,
            next: {
              id: next.id,
              start_date: next.start_date,
              customer_name: next.customer_name
            }
          };
          futureResMap[vehicleId] = futureRes;
        });

        setUpcomingStatuses(statusMap);
        setAllFutureReservations(futureResMap);
      } catch (err) {
        console.error('Failed to fetch upcoming reservations:', err);
      }
    };

    fetchUpcomingReservations();
  }, [selectedDate, returns, vehicles]);

  // 貸出リストの各行に対して、同日のそれ以前に返却があったか判定
  const finalDepartures = useMemo(() => {
    return departures.map(d => {
      // 当日、この出発時刻以前（同時刻含む）に返却があるか
      // 返却時刻 == 出発時刻（返却直後の即再出発）もターンアラウンドとして検知する
      const hasEarlierReturn = returns.some(r =>
        String(r.vehicle_id) === String(d.vehicle_id) &&
        !dayjs(r.end_date).isAfter(dayjs(d.start_date))
      );
      // 返却時刻と貸出時刻がぴったり同じ（間に余裕ゼロ）は別色で示す（2026-08-25）
      const hasSameTimeReturn = returns.some(r =>
        String(r.vehicle_id) === String(d.vehicle_id) &&
        dayjs(r.end_date).isSame(dayjs(d.start_date))
      );
      return { ...d, isTurnaroundDeparture: hasEarlierReturn, isSameTimeTurnaround: hasSameTimeReturn };
    });
  }, [departures, returns]);

  // 返却リストの各行に対して、それ以降に予約があるか判定（本日、明日、明後日）
  const finalReturns = useMemo(() => {
    const baseDate = dayjs(selectedDate);
    const startDate = baseDate.format('YYYY-MM-DD');
    const date1Str = baseDate.add(1, 'day').format('YYYY-MM-DD');
    const date2Str = baseDate.add(2, 'day').format('YYYY-MM-DD');

    return returns.map(r => {
      const vId = r.vehicle_id;
      const vehicleFutureRes = allFutureReservations[vId] || [];

      // この返却時刻以降（同時刻含む）の最も早い出発を探す
      // 出発時刻 == 返却時刻（返却直後の即再出発）もターンアラウンドとして検知する
      const next = vehicleFutureRes.find(res =>
        !dayjs(res.start_date).isBefore(dayjs(r.end_date))
      );

      let rowAttention = null;
      if (next) {
        const nextStartDate = dayjs(next.start_date).format('YYYY-MM-DD');
        if (nextStartDate === startDate) {
          // 返却時刻と同時刻の再貸出はショッキングピンク（2026-08-25）
          const sameTime = dayjs(next.start_date).isSame(dayjs(r.end_date));
          rowAttention = sameTime
            ? { color: TURNAROUND_SAME_TIME_COLOR, label: `返却と同時刻（${dayjs(next.start_date).format('HH:mm')}）に出る` }
            : { color: TURNAROUND_COLOR, label: `次${dayjs(next.start_date).format('HH:mm')}に出る` };
        } else if (nextStartDate === date1Str) {
          rowAttention = { color: '#1976d2', label: '翌日に予約あり' };
        } else if (nextStartDate === date2Str) {
          rowAttention = { color: '#03a9f4', label: '翌々日に予約あり' };
        }
      }
      return { ...r, rowAttention };
    });
  }, [returns, allFutureReservations, selectedDate]);

  // ============================================================
  // フライト情報取得 — 本日表示時のみ
  //   那覇: 空港が公開している FIS の JSON
  //   宮古: 空港トップページの「本日のフライト」を sys/api/miyako_flights.php で
  //         パースしたもの（JSON の口が無いため。2026-09-01 追加）
  // どちらも「本日ぶん」しか出ていないので、当日表示のときだけ有効にする
  // ============================================================
  const isMiyakoInstance = getInstance() === 'miyako';
  // ⚠️ 宮古は現在使えない（2026-09-03）。
  //   宮古空港サイト(miyakoap.co.jp)が **サーバの IP を 403 で拒否** するため、
  //   sys/api/miyako_flights.php が 0件しか返せない（ローカルからは取得できる＝IP遮断）。
  //   取得経路が決まったらこのフラグを true に戻すだけで有効になる。
  //   実装（PHPパーサ / miyakoFlightService / 照合）はすべて動作確認済み。
  const MIYAKO_FLIGHT_ENABLED = false;
  const isFlightFeatureAvailable = (!isMiyakoInstance || MIYAKO_FLIGHT_ENABLED)
    && selectedDate === dayjs().format('YYYY-MM-DD');
  const [flightMenuAnchor, setFlightMenuAnchor] = useState(null);
  const [flightData, setFlightData] = useState(null);
  // 予約ID → { arrival, matchedVia }（貸出リストの ARR 見込み表示用）
  const [flightInfoMap, setFlightInfoMap] = useState({});
  const [flightLoading, setFlightLoading] = useState(false);
  const [flightFixOpen, setFlightFixOpen] = useState(false);
  const [flightFixRows, setFlightFixRows] = useState([]);
  const [flightGeminiLoading, setFlightGeminiLoading] = useState(false);

  // FIS取得 + 貸出リストの到着便を照合
  const runFlightMatch = useCallback(async () => {
    const ymd = dayjs(selectedDate).format('YYYYMMDD');
    const data = isMiyakoInstance ? await fetchMiyakoFlights(ymd) : await fetchNahaFlights(ymd);
    const map = {};
    const unmatched = [];
    finalDepartures.forEach((d) => {
      const flightText = String(d.arrival_flight || d.detail?.arrival_flight || '').trim();
      if (!flightText) return;
      const parsed = parseReservationFlight(flightText, data.airlineAliasMap);
      const match = parsed ? findArrival(data.arrivals, parsed.carrier, parsed.flightNo) : null;
      if (match) {
        map[String(d.id)] = match;
      } else {
        unmatched.push({ id: String(d.id), name: d.customer_name || '', text: flightText });
      }
    });
    return { data, map, unmatched };
  }, [selectedDate, finalDepartures, isMiyakoInstance]);

  // 「フライト情報を更新する」: 照合してARR列の見込み時間を反映
  const handleFlightUpdate = async () => {
    setFlightMenuAnchor(null);
    setFlightLoading(true);
    try {
      const { data, map, unmatched } = await runFlightMatch();
      setFlightData(data);
      setFlightInfoMap(map);
      setToast({
        open: true,
        message: `フライト情報を更新しました（照合 ${Object.keys(map).length}件 / 未照合 ${unmatched.length}件 / 当日到着便 ${data.arrivals.length}件）`,
        severity: 'success',
      });
    } catch (e) {
      console.error('[Flight] update failed:', e);
      setToast({ open: true, message: e.message || 'フライト情報の取得に失敗しました', severity: 'error' });
    }
    setFlightLoading(false);
  };

  // フライト表示はデフォルトON（2026-07-26）:
  // 那覇・本日表示なら、貸出データ到達時に自動で「フライト情報を更新する」を実行する。
  // 手動更新と違いトーストは出さない（ページを開くたびに出ると煩いため）。
  // 日付ごとに1回だけ自動実行（手動🔄はいつでも可）。
  const flightAutoRunRef = useRef(null);
  useEffect(() => {
    if (!isFlightFeatureAvailable) return;
    if (finalDepartures.length === 0) return;
    if (flightAutoRunRef.current === selectedDate) return;
    flightAutoRunRef.current = selectedDate;
    (async () => {
      setFlightLoading(true);
      try {
        const { data, map } = await runFlightMatch();
        setFlightData(data);
        setFlightInfoMap(map);
      } catch (e) {
        console.error('[Flight] auto update failed:', e);
      }
      setFlightLoading(false);
    })();
  }, [isFlightFeatureAvailable, finalDepartures, selectedDate, runFlightMatch]);

  // 「フライト表示を修正する」: 未照合の予約を修正ダイアログに出す
  const handleFlightFixOpen = async () => {
    setFlightMenuAnchor(null);
    setFlightLoading(true);
    try {
      const { data, map, unmatched } = await runFlightMatch();
      setFlightData(data);
      setFlightInfoMap(map);
      setFlightFixRows(unmatched.map(u => ({ ...u, fisText: '', suggestion: '', reason: '', applied: false, saving: false })));
      setFlightFixOpen(true);
    } catch (e) {
      console.error('[Flight] fix-open failed:', e);
      setToast({ open: true, message: e.message || 'フライト情報の取得に失敗しました', severity: 'error' });
    }
    setFlightLoading(false);
  };

  // Gemini に表記ゆらぎの修正案を出させる
  const handleRunGemini = async () => {
    if (!flightData) return;
    setFlightGeminiLoading(true);
    try {
      const targets = flightFixRows.filter(r => !r.applied).map(r => ({ id: r.id, text: r.text }));
      const suggestions = await fetchGeminiFlightFixes(targets, flightData.arrivals);
      setFlightFixRows(prev => prev.map(r => {
        const s = suggestions.find(x => String(x.id) === String(r.id));
        if (!s) return r;
        let fisText = r.fisText;
        let suggestion = s.suggestion || '';
        // Gemini が carrier+flight_no を返した場合、実在便を照合して URL表記を埋め、
        // suggestion が空でも実在すれば正規化表記を自動補完する
        if (s.carrier && s.flight_no) {
          const m = findArrival(flightData.arrivals, String(s.carrier).toUpperCase(), Number(s.flight_no));
          if (m) {
            const est = m.arrival.estimated || m.arrival.actual;
            fisText = `${m.arrival.carrier}${m.arrival.flightNo} ${m.arrival.originJp}発 定刻${m.arrival.scheduled || '-'}${est ? ` / 見込${est}` : ''}${m.arrival.statusJp ? ` [${m.arrival.statusJp}]` : ''}`;
            if (!suggestion) suggestion = `${m.arrival.carrier}${m.arrival.flightNo}`;
          }
        }
        return { ...r, suggestion, fisText, reason: s.reason || '' };
      }));
    } catch (e) {
      console.error('[Flight] gemini failed:', e);
      setToast({ open: true, message: e.message || 'Gemini修正案の取得に失敗しました', severity: 'error' });
    }
    setFlightGeminiLoading(false);
  };

  // 修正案を予約に適用（arrival_flight を書き換え + memoに変更履歴を追記）
  const handleApplyFlightFix = async (fixRow) => {
    const reservation = finalDepartures.find(d => String(d.id) === String(fixRow.id));
    const newVal = String(fixRow.suggestion || '').trim();
    if (!reservation || !newVal) return;

    setFlightFixRows(prev => prev.map(r => r.id === fixRow.id ? { ...r, saving: true } : r));
    try {
      const oldVal = String(reservation.arrival_flight || reservation.detail?.arrival_flight || '');
      const timestamp = dayjs().format('YYYY-MM-DD HH:mm');
      const memoEntry = `---フライト表記修正 ${timestamp}---\n到着便: ${oldVal || '(空)'} → ${newVal}\n---`;
      const currentMemo = reservation.detail?.memo || reservation.memo || '';
      const updatedMemo = currentMemo ? `${currentMemo}\n${memoEntry}` : memoEntry;

      // 重要: reservation 全体を spread して既存フィールドを保持する（部分更新の原則）
      await saveDispatchReservation({
        ...reservation,
        arrival_flight: newVal,
        detail: {
          ...(reservation.detail || {}),
          arrival_flight: newVal,
          memo: updatedMemo,
        },
      });

      // 修正後の表記で再照合し、ARR見込みにも即反映
      if (flightData) {
        const parsed = parseReservationFlight(newVal, flightData.airlineAliasMap);
        const match = parsed ? findArrival(flightData.arrivals, parsed.carrier, parsed.flightNo) : null;
        if (match) {
          setFlightInfoMap(prev => ({ ...prev, [String(reservation.id)]: match }));
        }
      }

      setFlightFixRows(prev => prev.map(r => r.id === fixRow.id ? { ...r, saving: false, applied: true } : r));
      setToast({ open: true, message: `${reservation.customer_name || '予約'} の到着便を「${newVal}」に修正しました`, severity: 'success' });
    } catch (e) {
      console.error('[Flight] apply failed:', e);
      setFlightFixRows(prev => prev.map(r => r.id === fixRow.id ? { ...r, saving: false } : r));
      setToast({ open: true, message: '予約の更新に失敗しました', severity: 'error' });
    }
  };

  // 表示中の予約に紐づく LINE ユーザーを補った一覧（2026-10-10）。
  // useLineChat の lineUsers は get_users の「非アーカイブ上位500人」しか無く、最近やり取りの無い顧客が
  // 「LINE未連携」と誤表示されていた。表に渡すのはこちら（ピン留めスタッフ等は従来の lineUsers のまま）
  const displayReservationIds = useMemo(
    () => [...finalDepartures, ...finalReturns].map((r: any) => r?.id),
    [finalDepartures, finalReturns],
  );
  const displayLineUsers = useLineUsersForReservations(lineUsers, displayReservationIds);

  // ============================================================
  // 統合リスト表示用データ: 貸出+返却を時間順に混ぜ、
  // 同一車両の「返却 → 30分以内の貸出」をペア連結し、30分グリッドの見出しを付与
  // ============================================================
  const mergedListData = useMemo(() => {
    if (!mergedView) return [];
    const rows = [
      ...finalDepartures.map(d => ({ ...d, __listType: 'departure', __t: d.start_date })),
      ...finalReturns.map(r => ({ ...r, __listType: 'return', __t: r.end_date })),
    ]
      .filter(r => r.__t)
      .sort((a, b) => {
        const c = String(a.__t).localeCompare(String(b.__t));
        if (c !== 0) return c;
        // 同時刻は返却を優先的に上に
        if (a.__listType !== b.__listType) return a.__listType === 'return' ? -1 : 1;
        return 0;
      });

    // ターンアラウンドペア検出: 返却ごとに「同一車両・返却時刻の0〜30分後に出発する貸出」を探す
    const pairedDepIds = new Set();
    const pairDepOfReturn = {};
    rows.forEach(ret => {
      if (ret.__listType !== 'return' || !ret.vehicle_id) return;
      const retT = dayjs(ret.__t);
      const dep = rows.find(d =>
        d.__listType === 'departure' &&
        !pairedDepIds.has(d.id) &&
        String(d.vehicle_id) === String(ret.vehicle_id) &&
        (() => { const diff = dayjs(d.__t).diff(retT, 'minute'); return diff >= 0 && diff <= 30; })()
      );
      if (dep) {
        pairedDepIds.add(dep.id);
        pairDepOfReturn[ret.id] = dep;
      }
    });

    // ペアの貸出を返却の直後に並べ替え
    const out = [];
    const placed = new Set();
    rows.forEach(r => {
      const key = `${r.__listType}-${r.id}`;
      if (placed.has(key)) return;
      if (r.__listType === 'departure' && pairedDepIds.has(r.id)) return; // ペアの返却側で配置する
      out.push(r);
      placed.add(key);
      if (r.__listType === 'return' && pairDepOfReturn[r.id]) {
        const dep = pairDepOfReturn[r.id];
        out.push(dep);
        placed.add(`departure-${dep.id}`);
      }
    });

    // ペアフラグ + 30分グリッド見出し + スロット交互背景（ゼブラ）用のパリティ
    let prevSlot = null;
    let slotIndex = -1;
    out.forEach(r => {
      r.__pairWithNext = r.__listType === 'return' && !!pairDepOfReturn[r.id];
      r.__pairWithPrev = r.__listType === 'departure' && pairedDepIds.has(r.id);
      if (r.__pairWithPrev) {
        // ペアの貸出側は返却側のスロットにぶら下がる（見出しは出さず、同じ帯色）
        r.__slotLabel = null;
        r.__slotEven = slotIndex >= 0 && slotIndex % 2 === 0;
        return;
      }
      const t = dayjs(r.__t);
      const slot = `${t.format('HH')}:${t.minute() < 30 ? '00' : '30'}`;
      if (slot !== prevSlot) {
        slotIndex += 1;
        prevSlot = slot;
        r.__slotLabel = slot;
      } else {
        r.__slotLabel = null;
      }
      r.__slotEven = slotIndex % 2 === 0;
    });
    return out;
  }, [mergedView, finalDepartures, finalReturns]);

  const formatTime = (isoString) => {
    if (!isoString) return '';
    return dayjs(isoString).format('HH:mm');
  };

  // 車両チェック項目更新ハンドラー
  const handleCheckUpdate = async (vehicle, checkItems) => {
    if (!vehicle || !vehicle.id) return;

    // Optimistic update
    const updatedVehicle = {
      ...vehicle,
      check_situations: checkItems,
    };
    setVehicles(prev => ({ ...prev, [vehicle.id]: updatedVehicle }));

    try {
      const result = await updateVehicle({
        id: vehicle.id,
        check_situations: checkItems.map(item => ({
          id: item.id,
          value: item.value,
        })),
      }, user?.id);

      if (result) {
        setVehicles(prev => ({ ...prev, [result.id]: result }));
        setToast({ open: true, message: 'チェック項目を更新しました', severity: 'success' });
      }
    } catch (error) {
      console.error('Failed to update check items:', error);
      setToast({ open: true, message: 'チェック項目の更新に失敗しました', severity: 'error' });
      // Revert
      setVehicles(prev => ({ ...prev, [vehicle.id]: vehicle }));
    }
  };

  // 現在のリストタイプに応じた位置ステータス選択肢を取得
  const getLocationStatusOptions = (listType) => {
    // showInフィールドは削除されたため、すべての位置ステータスを返す
    // locationStatusList は useMemo で常に配列を返すため、フォールバック不要
    return locationStatusList;
  };

  // スタッフ選択クリックハンドラー
  const handleStaffClick = (reservation, staffType, listType) => {
    setStaffModal({
      open: true,
      reservation,
      staffType,  // 'driver' or 'handler'
      listType    // 'departure' or 'return'
    });
  };

  // スタッフ選択ハンドラー
  const handleStaffSelect = async (staff) => {
    const { reservation, staffType, listType } = staffModal;
    if (!reservation) return;

    setStaffModal({ open: false, reservation: null, staffType: null, listType: null });

    // ピン留めスタッフかどうか判定
    const isPinnedStaff = staff && staff._isPinnedStaff;

    // スタッフフィールドを決定
    // 貸出(departure): driver -> pu_staff_id, handler -> rent_staff_id
    // 返却(return): driver -> do_staff_id, handler -> return_staff_id
    let staffField;
    if (listType === 'departure') {
      staffField = staffType === 'driver' ? 'pu_staff_id' : 'rent_staff_id';
    } else {
      staffField = staffType === 'driver' ? 'do_staff_id' : 'return_staff_id';
    }

    // ピン留めスタッフの場合はID:14で登録、表示名はスタッフ登録名
    const staffId = staff ? (isPinnedStaff ? 14 : staff.id) : null;
    const displayName = staff ? (isPinnedStaff ? staff.staffName : staff.staff_name) : null;

    console.log('[DayList] handleStaffSelect called:', {
      reservationId: reservation.id,
      customerName: reservation.customer_name,
      staffType,
      listType,
      isPinnedStaff,
      staffId,
      displayName,
      staffField,
      originalStaff: staff,
    });

    // 表示用のフィールド名を決定
    const displayField = staffField.replace('_id', '');

    // Optimistic update
    const updatedReservation = {
      ...reservation,
      [displayField]: staff ? { id: staffId, name: displayName, staff_name: displayName, active: staff.active ?? 1, active_name: staff.active_name ?? '在籍', is_deleted: staff.is_deleted ?? false } : null
    };

    console.log('[DayList] Optimistic update:', {
      displayField,
      updatedValue: updatedReservation[displayField],
      fullField: `${displayField}.id=${staffId}, name=${displayName}`,
    });

    const updateRow = (r) => {
      if (String(r.id) !== String(reservation.id)) return r;
      return updatedReservation;
    };

    if (listType === 'departure') {
      setDepartures(prev => prev.map(updateRow));
    } else {
      setReturns(prev => prev.map(updateRow));
    }

    try {
      // reservationオブジェクト全体を渡す（APIに必須フィールドを含めるため）
      await saveReservationStaff(reservation, staffField, staffId);
      console.log('[DayList] saveReservationStaff success:', { staffField, staffId });
      const staffLabel = staffType === 'driver' ? '運転者' : '対応者';
      setToast({
        open: true,
        message: staff ? `${staffLabel}を${displayName}に設定しました` : `${staffLabel}を解除しました`,
        severity: 'success'
      });

      // ピン留めスタッフの場合: メモに記録 + LINEメッセージ送信
      if (isPinnedStaff) {
        const timestamp = dayjs().format('YYYY-MM-DD HH:mm');
        const staffLabel2 = listType === 'departure' ? '貸出運転者' : '返却運転者';
        const memoEntry = `---${staffLabel2}アサイン ${timestamp}---\n${staff.staffName}\n---`;
        const currentMemo = reservation.detail?.memo || reservation.memo || '';
        const updatedMemo = currentMemo ? `${currentMemo}\n${memoEntry}` : memoEntry;

        // メモを保存
        // 重要: reservation 全体を spread して既存の detail / car_type / vehicle_id 等を保持する
        // （bug fix: 過去に detail なしで save していたため、base_price / sum_price / car_type が
        //  バックエンドで 0 / '' にリセットされる事象が発生していた）
        try {
          await saveDispatchReservation({
            ...reservation,
            customer_name: reservation.customer_name || '予約',
            detail: {
              ...(reservation.detail || {}),
              memo: updatedMemo,
            },
          });
        } catch (e) {
          console.error('Failed to save memo for pinned staff:', e);
        }

        // LINEメッセージ送信
        const vehicle = vehicles[String(reservation.vehicle_id)];
        const vehicleCode = vehicle?.vehicle_code || reservation.vehicle_id || '未割当';
        const typeLabel = listType === 'departure' ? '[🚙 ピックアップ]' : '[🚗 ドロップオフ]';
        const lineMessage = `${typeLabel}\n車両: ${vehicleCode}\n顧客名: ${reservation.customer_name || ''}`;
        try {
          await sendMessage(staff.line_user_id, lineMessage);
        } catch (e) {
          console.error('Failed to send LINE message to pinned staff:', e);
        }
        // N仕事（LINE WORKS）にも同内容を送信（2026-07-12。失敗しても本処理は止めない）
        try {
          await sendLineWorksNotify(`${lineMessage}\n担当: ${staff.staffName}`);
        } catch (e) {
          console.error('Failed to send LINE WORKS notify:', e);
        }
      }
    } catch (error) {
      console.error('Failed to update staff:', error);
      setToast({ open: true, message: 'スタッフの更新に失敗しました', severity: 'error' });
      // Revert
      if (listType === 'departure') {
        setDepartures(prev => prev.map(r => String(r.id) === String(reservation.id) ? reservation : r));
      } else {
        setReturns(prev => prev.map(r => String(r.id) === String(reservation.id) ? reservation : r));
      }
    }
  };

  // 免許確認トグルハンドラ
  const handleLicenseToggle = async (reservation, listType) => {
    // 現在の免許確認状態を取得（detailオブジェクト内）
    const currentLicense = Number(reservation.detail?.license ?? 0);
    const newLicense = currentLicense === 1 ? 0 : 1;

    // Optimistic update
    const updatedReservation = {
      ...reservation,
      detail: {
        ...reservation.detail,
        license: newLicense,
      }
    };

    const updateRow = (r) => {
      if (String(r.id) !== String(reservation.id)) return r;
      return updatedReservation;
    };

    if (listType === 'departure') {
      setDepartures(prev => prev.map(updateRow));
    } else {
      setReturns(prev => prev.map(updateRow));
    }

    try {
      await toggleReservationLicense(reservation, newLicense);
      setToast({
        open: true,
        message: newLicense === 1 ? '免許確認済みに更新しました' : '免許未確認に更新しました',
        severity: 'success'
      });
    } catch (error) {
      console.error('Failed to toggle license:', error);
      setToast({ open: true, message: '免許確認の更新に失敗しました', severity: 'error' });
      // Revert
      if (listType === 'departure') {
        setDepartures(prev => prev.map(r => String(r.id) === String(reservation.id) ? reservation : r));
      } else {
        setReturns(prev => prev.map(r => String(r.id) === String(reservation.id) ? reservation : r));
      }
    }
  };

  // 連絡手段更新ハンドラ
  const handleCommunicationToolsUpdate = async (reservation, tools, listType) => {
    // Optimistic update
    const updatedReservation = {
      ...reservation,
      detail: {
        ...reservation.detail,
        communication_tools: tools,
      }
    };

    const updateRow = (r) => {
      if (String(r.id) !== String(reservation.id)) return r;
      return updatedReservation;
    };

    if (listType === 'departure') {
      setDepartures(prev => prev.map(updateRow));
    } else {
      setReturns(prev => prev.map(updateRow));
    }

    try {
      await updateReservationCommunicationTools(reservation, tools);
      setToast({
        open: true,
        message: '連絡手段を更新しました',
        severity: 'success'
      });
    } catch (error) {
      console.error('Failed to update communication tools:', error);
      setToast({ open: true, message: '連絡手段の更新に失敗しました', severity: 'error' });
      // Revert
      if (listType === 'departure') {
        setDepartures(prev => prev.map(r => String(r.id) === String(reservation.id) ? reservation : r));
      } else {
        setReturns(prev => prev.map(r => String(r.id) === String(reservation.id) ? reservation : r));
      }
    }
  };

  const isActive = (r) => {
    const s = String(r.status);
    return s !== '4' && s !== '5' && s !== 'cancelled' && s !== 'nonreserved' && s !== 'full' && s !== 'offer';
  };

  // 車両ステータスを更新
  const handleUpdateVehicleStatus = useCallback(async (vehicleId, statusId) => {
    if (!vehicleId) return;
    try {
      const statusInfo = vehicleStatusList.find(s => s.id === statusId);
      await updateVehicle({
        id: vehicleId,
        status: statusId,
      }, user?.id);

      // ローカル状態を更新
      setVehicles(prev => ({
        ...prev,
        [String(vehicleId)]: {
          ...prev[String(vehicleId)],
          status: statusId,
          status_name: statusInfo?.label || '',
        },
      }));
      setToast({ open: true, message: '車両ステータスを更新しました', severity: 'success' });
    } catch (err) {
      console.error('Failed to update vehicle status:', err);
      setToast({ open: true, message: 'ステータスの更新に失敗しました', severity: 'error' });
      throw err; // Re-throw to allow modal to handle error if needed
    }
  }, [user?.id, vehicleStatusList]);

  // 車両アサインモーダルを開く
  const handleOpenAssignModal = useCallback((reservation) => {
    setAssignTargetReservation(reservation);
    setAssignModalOpen(true);
  }, []);

  // 車両アサインモーダルを閉じる
  const handleCloseAssignModal = useCallback(() => {
    setAssignModalOpen(false);
    setAssignTargetReservation(null);
  }, []);

  // 車両アサイン実行
  const executeAssignment = useCallback(async (reservation, vehicleId) => {
    try {
      // アサインする車両情報を取得
      const assignedVehicle = vehicles[String(vehicleId)];
      const updatePayload = {
        ...reservation,
        vehicle_id: vehicleId,
      };

      // 車両のアサイン時に車種を変更しないようにするため、
      // updatePayload.car_type の自動更新を削除しました。

      const savedReservation = await saveDispatchReservation(updatePayload);

      // ローカルステートを更新
      const updateRow = r => {
        if (String(r.id) === String(reservation.id)) {
          const updated = { ...savedReservation };
          if (updated.vehicle_id && vehicles[String(updated.vehicle_id)]) {
            updated.vehicle_code = vehicles[String(updated.vehicle_id)].vehicle_code;
          }
          return updated;
        }
        return r;
      };

      setDepartures(prev => prev.map(updateRow));
      setReturns(prev => prev.map(updateRow));

      handleCloseAssignModal();
      setToast({ open: true, message: '車両をアサインしました', severity: 'success' });
    } catch (err) {
      console.error('Failed to assign vehicle:', err);
      setToast({ open: true, message: '車両のアサインに失敗しました', severity: 'error' });
    }
  }, [vehicles, handleCloseAssignModal]);

  // 支払確認ボタンクリックハンドラ（DS1をメインデータソースとして使用）
  // 支払い済みフラグ更新の共通処理（モーダルのボタン / Square自動確認から共用。2026-07-26）
  const handleSetPaymentStatus = useCallback(async (reservation, newPaymentStatus, opts: { auto?: boolean; squareUnpaid?: boolean } = {}) => {
    try {
      // reservation 全体を spread して car_type / vehicle_id を保持（過去のクリア事故対策）
      const saved = await saveDispatchReservation({
        ...reservation,
        customer_name: reservation.customer_name || '予約',
        detail: { ...(reservation.detail || {}), payment: newPaymentStatus },
      });
      if (saved) reservationSyncService.applyLocalUpdate(saved);

      recordChange(
        'reservation',
        reservation.id,
        { ...reservation, detail: { ...(reservation.detail || {}), payment: newPaymentStatus === 1 ? 0 : 1 } },
        { ...reservation, detail: { ...(reservation.detail || {}), payment: newPaymentStatus } },
        'user'
      );

      const updateRow = (r) => {
        if (String(r.id) !== String(reservation.id)) return r;
        return { ...r, detail: { ...(r.detail || {}), payment: newPaymentStatus } };
      };
      setDepartures(prev => prev.map(updateRow));
      setReturns(prev => prev.map(updateRow));
      setSquareModal(prev => (prev.reservation && String(prev.reservation.id) === String(reservation.id))
        ? { ...prev, reservation: { ...prev.reservation, detail: { ...(prev.reservation.detail || {}), payment: newPaymentStatus } } }
        : prev);

      if (opts.auto) {
        setToast({ open: true, message: `${reservation.customer_name || ''} 様: Squareで支払いを確認したため「支払い済み」に設定しました`, severity: 'success' });
      } else if (newPaymentStatus === 1 && opts.squareUnpaid) {
        setToast({ open: true, message: '支払い済みにしました（注意: Squareでは支払い済みになっていません）', severity: 'warning' });
      } else {
        setToast({ open: true, message: newPaymentStatus === 1 ? '支払確認済みに更新しました' : '未払いに戻しました', severity: 'success' });
      }
    } catch (error) {
      console.error('Failed to update payment status:', error);
      setToast({ open: true, message: '更新に失敗しました: ' + error.message, severity: 'error' });
    }
  }, [recordChange]);

  // Square で全 Invoice 支払い済みが確認できたら、支払い済みフラグを自動でONにする（2026-07-26）
  // モーダルを開いてデータが揃ったタイミングで1回だけ実行（予約ごとにガード）
  const autoPaidMarkedRef = useRef({});
  useEffect(() => {
    if (!squareModal.open || squareModal.loading || !squareModal.reservation) return;
    const r = squareModal.reservation;
    if (Number(r.detail?.payment) === 1) return; // 既にON
    if (!isSquarePaid(squareModal.paymentData)) return;
    const key = String(r.id);
    if (autoPaidMarkedRef.current[key]) return;
    autoPaidMarkedRef.current[key] = true;
    handleSetPaymentStatus(r, 1, { auto: true });
  }, [squareModal, handleSetPaymentStatus]);

  const handlePaymentCheck = useCallback(async (reservation) => {
    const contactCode = reservation.contact_code || reservation.detail?.contact_id;
    if (!contactCode) {
      setToast({ open: true, message: '予約番号が見つかりません', severity: 'warning' });
      return;
    }

    // キャッシュからデータを取得
    const cached = getPaymentData(contactCode);

    // ダイアログを表示
    setSquareModal({
      open: true,
      reservation,
      paymentData: cached || null,
      loading: !cached,
    });

    // キャッシュがない場合はDS1から取得
    if (!cached) {
      try {
        await refreshSingleContact(contactCode);
      } catch (error) {
        console.error('Failed to fetch payment data on demand:', error);
      }
    }
  }, [getPaymentData, refreshSingleContact]);

  // squarePaymentCache が更新されたらモーダルのデータも同期
  useEffect(() => {
    if (!squareModal.open || !squareModal.reservation) return;
    const contactCode = squareModal.reservation.contact_code || squareModal.reservation.detail?.contact_id;
    if (!contactCode) return;
    const entry = squarePaymentCache[contactCode];
    if (entry) {
      setSquareModal(prev => ({ ...prev, paymentData: entry, loading: false }));
    }
  }, [squarePaymentCache, squareModal.open, squareModal.reservation]);

  // スタッフの表示用フィルタリング（在籍中、または現在割り当て済みのスタッフ）
  const getFilteredStaffList = (_currentStaffId) => {
    if (!Array.isArray(staffList)) return [];
    return staffList
      .filter(staff => {
        // システムアカウント（APIテスト用・外部API専用）は常に非表示。
        // ID 決め打ちだと店舗ごとの採番差で漏れる（宮古は id=3,4 が該当）
        if (isSystemAccount(staff)) return false;
        // is_active は NULL/undefined のスタッフもあるため、明示的な 0 のみ除外
        return staff.is_active !== 0 && !staff.is_deleted && isOnDuty(staff.priority);
      })
      .sort((a, b) => (b.priority || 0) - (a.priority || 0));
  };

  // 現在の予約に割り当てられているスタッフIDを取得
  const getCurrentStaffId = () => {
    const { reservation, staffType, listType } = staffModal;
    if (!reservation) return null;

    let staffObj;
    if (listType === 'departure') {
      staffObj = staffType === 'driver' ? reservation.pu_staff : reservation.rent_staff;
    } else if (listType === 'return') {
      staffObj = staffType === 'driver' ? reservation.do_staff : reservation.return_staff;
    }
    return staffObj?.id || null;
  };

  // 予約保存ハンドラー
  const handleReservationSave = async (reservation) => {
    try {
      const savedReservation = await saveDispatchReservation(reservation);
      setToast({ open: true, message: '予約を保存しました', severity: 'success' });

      // 同期キャッシュへ即時反映（ダイジェスト・車両ポジション等の他画面も更新される）
      if (savedReservation) reservationSyncService.applyLocalUpdate(savedReservation);

      // ローカルステートを直接更新
      // 編集データからdetailオブジェクトを構築
      // 2026-07-26 修正: 確認フラグ(payment/license等)がこの手組みリストから漏れていて、
      // パネルで支払い確認をONにしてもリストの[支]が未反映のままになる不具合があった。
      // フラグは undefined 時に既存値を消さないようガード付きで含める。
      const flagIfProvided = (key) => (reservation[key] !== undefined ? { [key]: reservation[key] } : {});
      const updatedDetail = {
        pax_children: reservation.pax_children,
        child_seat: reservation.child_seat,
        booster_seat: reservation.booster_seat,
        arrival_flight: reservation.arrival_flight,
        departure_flight: reservation.departure_flight,
        comment: reservation.comment,
        memo: reservation.memo,
        options: reservation.options,
        bk: reservation.bk,
        contact_plan: reservation.contact_plan,
        communication_tools: reservation.communication_tools,
        ...flagIfProvided('license'),
        ...flagIfProvided('payment'),
        ...flagIfProvided('agreement'),
        ...flagIfProvided('terms_and_conditions'),
        ...flagIfProvided('disclaimer'),
      };

      // reservation.idは文字列の場合があるので、比較時に型を揃える
      const reservationId = reservation.id ? String(reservation.id) : null;
      const isNew = !reservationId || reservationId.startsWith('tmp_');

      if (isNew) {
        // 新規作成（コピー含む）の場合
        const newRow = {
          ...reservation,
          // NOTE: Fallback required for spread - savedReservation may be null/undefined
          ...(savedReservation || {}),
          // NOTE: Fallback required for spread. Detail is validated non-null by SyncService, but kept for runtime safety.
          detail: { ...(reservation.detail || {}), ...updatedDetail }
        };

        // 貸出リストに追加（日付が一致する場合）
        if (newRow.start_date && newRow.start_date.substring(0, 10) === selectedDate) {
          setDepartures(prev => [newRow, ...prev]);
        }
        // 返却リストに追加（日付が一致する場合）
        if (newRow.end_date && newRow.end_date.substring(0, 10) === selectedDate) {
          setReturns(prev => [newRow, ...prev]);
        }
      } else {
        // 既存の更新
        const updateRow = (r) => {
          if (String(r.id) !== reservationId) return r;
          return {
            ...r,
            // NOTE: Fallback required for spread - savedReservation may be null/undefined
            ...(savedReservation || {}),
            pax: reservation.pax,
            customer_name: reservation.customer_name || r.customer_name,
            // Update confirmation flags at top level as well to ensure display updates
            license: reservation.license,
            payment: reservation.payment,
            agreement: reservation.agreement,
            terms_and_conditions: reservation.terms_and_conditions,
            disclaimer: reservation.disclaimer,
            short_memo: reservation.short_memo,
            request_coming_time: reservation.request_coming_time,
            request_returning_time: reservation.request_returning_time,

            // NOTE: Fallback required for spread. Detail is validated non-null by SyncService, but kept for runtime safety.
            // サーバレスポンスの detail（正本）も重ね、手組みリストの漏れによる未反映を防ぐ（2026-07-26）
            detail: { ...(r.detail || {}), ...updatedDetail, ...((savedReservation && savedReservation.detail) || {}) }
          };
        };

        // departures（貸出）リストを更新
        setDepartures(prev => prev.map(updateRow));
        // returns（返却）リストを更新
        setReturns(prev => prev.map(updateRow));
      }

    } catch (error) {
      console.error('Failed to save reservation:', error);
      setToast({ open: true, message: '予約の保存に失敗しました', severity: 'error' });
      throw error;
    }
  };

  // 簡易更新ハンドラー（short_memo, request_time等）
  const handleQuickUpdate = async (reservation, updates) => {
    try {
      // 既存の予約情報にupdatesをマージ
      // updatesは { short_memo: '...' } や { request_coming_time: '...' } など
      const payload = {
        ...reservation,
        ...updates,
        // detailなどはそのまま維持するか、サーバー側でマージされることを期待
        // saveDispatchReservationの実装依存だが、基本はフルオブジェクト送信か
        // ここではreservationをベースにupdatesを上書きして送信
      };

      const savedReservation = await saveDispatchReservation(payload);
      setToast({ open: true, message: '更新しました', severity: 'success' });

      const updateRow = (r) => {
        if (String(r.id) !== String(reservation.id)) return r;
        return {
          ...r,
          // NOTE: Fallback required for spread - savedReservation may be null/undefined
          ...(savedReservation || {}),
          // 念のためupdatesの内容も反映（レスポンスに含まれない場合への保険）
          ...updates
        };
      };

      setDepartures(prev => prev.map(updateRow));
      setReturns(prev => prev.map(updateRow));
    } catch (error) {
      console.error('Failed to quick update:', error);
      setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
    }
  };

  // 車両DnDスワップ: ドロップ時に確認Dialogを表示
  const handleVehicleSwapRequest = useCallback((sourceRow, targetRow) => {
    const srcVid = sourceRow.vehicle_id;
    const tgtVid = targetRow.vehicle_id;
    if (!srcVid || !tgtVid || String(srcVid) === String(tgtVid)) return;
    setVehicleSwapConfirm({ source: sourceRow, target: targetRow });
  }, []);

  // 車両DnDスワップ: 確認後に実行
  const executeVehicleSwap = useCallback(async () => {
    if (!vehicleSwapConfirm) return;
    const { source, target } = vehicleSwapConfirm;
    const srcVid = source.vehicle_id;
    const tgtVid = target.vehicle_id;
    setVehicleSwapConfirm(null);

    // 楽観的更新
    const swapRow = (r) => {
      if (String(r.id) === String(source.id)) return { ...r, vehicle_id: tgtVid };
      if (String(r.id) === String(target.id)) return { ...r, vehicle_id: srcVid };
      return r;
    };
    setDepartures(prev => prev.map(swapRow));
    setReturns(prev => prev.map(swapRow));

    try {
      await Promise.all([
        saveDispatchReservation({ ...source, vehicle_id: tgtVid }),
        saveDispatchReservation({ ...target, vehicle_id: srcVid }),
      ]);
      setToast({ open: true, message: '車両を入替しました', severity: 'success' });
    } catch (error) {
      console.error('Vehicle swap failed:', error);
      const rollback = (r) => {
        if (String(r.id) === String(source.id)) return { ...r, vehicle_id: srcVid };
        if (String(r.id) === String(target.id)) return { ...r, vehicle_id: tgtVid };
        return r;
      };
      setDepartures(prev => prev.map(rollback));
      setReturns(prev => prev.map(rollback));
      setToast({ open: true, message: '車両の入替に失敗しました', severity: 'error' });
    }
  }, [vehicleSwapConfirm]);

  // 最終更新時刻
  const [lastUpdated, setLastUpdated] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });

  // 手動更新ハンドラー
  const handleManualRefresh = useCallback(() => {
    reservationSyncService.forceSync();
    vehicleSyncService.forceSync();
    setRefreshKey(prev => prev + 1);
    const now = new Date();
    setLastUpdated(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);
    setToast({ open: true, message: '最新データに更新しました', severity: 'success' });
  }, []);

  return (
    // minWidth: fit-content（デスクトップのみ）: 貸出/返却リストの最低幅(600px×2)を下回る
    // ウィンドウ幅では中身を潰さず、ページ全体を横スクロールさせる（2026-08-10）
    <Box sx={{ flexGrow: 1, p: { xs: 1, md: 2 }, position: 'relative', minWidth: { xs: 'auto', md: 'fit-content' } }}>
      {/* トレーニングモードの帯（2026-08-24）。本番と取り違えないよう常時表示する */}
      {isTrainingMode() && (
        <Box sx={{
          position: 'sticky', top: 0, zIndex: 120,
          mx: { xs: -1, md: -2 }, px: { xs: 1, md: 2 }, py: 0.75,
          display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
          backgroundColor: '#b45309', color: '#fff',
          backgroundImage: 'repeating-linear-gradient(45deg, rgba(0,0,0,0.10) 0 12px, transparent 12px 24px)',
        }}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>
            トレーニングモード
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', opacity: 0.95 }}>
            ダミーデータです。ここでの操作は保存されず、本番の予約・車両には影響しません。
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="contained"
            color="inherit"
            sx={{ color: '#b45309', backgroundColor: '#fff', fontWeight: 700 }}
            onClick={() => {
              resetTrainingData();
              reservationSyncService.forceSync();
              vehicleSyncService.forceSync();
              setRefreshKey(prev => prev + 1);
              setToast({ open: true, message: 'トレーニングデータを初期状態に戻しました', severity: 'success' });
            }}
          >
            データを初期化
          </Button>
          <Button
            size="small"
            variant="outlined"
            sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.7)' }}
            onClick={() => navigate('/daylist')}
          >
            本番に戻る
          </Button>
        </Box>
      )}
      {/* 日付選択 + タブ（スマホ時）を1つの sticky ブロックにまとめる */}
      <Box data-daylist-sticky sx={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        bgcolor: 'background.paper',
        mx: { xs: -1, md: -2 },
      }}>
        <Box sx={{
          display: 'flex',
          // 左: 日付選択(上)+ボタン群(下) / 右: ダイジェスト・実況を右寄せで整列（2026-07-26）
          alignItems: { xs: 'flex-start', sm: 'stretch' },
          justifyContent: { xs: 'space-between', sm: 'flex-start' },
          gap: 1,
          py: 1,
          px: { xs: 1, md: 2 },
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}>
          {/* 左カラム: 日付選択(上) + 操作ボタン群(下)。右のウィンドウと上下端を揃える */}
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 0.75 }}>
          {/* 日付選択 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* 「旧版へ」ボタンは 2026-08-04 廃止（旧版 daylist_old の廃止に伴い） */}
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const today = dayjs().format('YYYY-MM-DD');
                setSelectedDate(today);
                // ?vehicle= 等の他クエリを消さないよう date だけ更新
                setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('date', today); return p; });
              }}
              sx={{
                minWidth: 'auto',
                px: 1,
                py: 0.5,
                fontSize: '0.75rem',
                fontWeight: 600,
                lineHeight: 1.2,
              }}
            >
              本日
            </Button>
            <IconButton
              size="small"
              onClick={() => {
                const prevDay = dayjs(selectedDate).subtract(1, 'day').format('YYYY-MM-DD');
                setSelectedDate(prevDay);
                setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('date', prevDay); return p; });
              }}
              sx={{ bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}
            >
              <Typography sx={{ fontSize: 16, fontWeight: 'bold' }}>&lt;</Typography>
            </IconButton>
            <TextField
              type="date"
              value={selectedDate}
              onChange={(e) => {
                const val = e.target.value;
                if (val) {
                  setSelectedDate(val);
                  setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('date', val); return p; });
                }
              }}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ width: 180 }}
            />
            <IconButton
              size="small"
              onClick={() => {
                const nextDay = dayjs(selectedDate).add(1, 'day').format('YYYY-MM-DD');
                setSelectedDate(nextDay);
                setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('date', nextDay); return p; });
              }}
              sx={{ bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}
            >
              <Typography sx={{ fontSize: 16, fontWeight: 'bold' }}>&gt;</Typography>
            </IconButton>
          </Box>

          {/* 操作ボタン群（同期・統合・コンパクト・フライト・更新）: 日付選択の下（2026-07-26 移動） */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            {!isMobile && lastSyncLabel && (
              <Typography
                variant="caption"
                title="最終同期時刻（ポーリング/Pusher による最新データ取得時刻）"
                sx={{ fontSize: 10, color: UI.color.textSub, whiteSpace: 'nowrap', alignSelf: 'center', mr: 0.5 }}
              >
                同期 {lastSyncLabel}
              </Typography>
            )}
            {!isMobile && (
              <Tooltip title={mergedView ? '貸出・返却を2列表示に戻す' : '貸出・返却を1つのリストで表示（時間順）'}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const next = !mergedView;
                      setMergedView(next);
                      document.cookie = `daylist_merged=${next}; path=/; max-age=${60 * 60 * 24 * 365}`;
                    }}
                    sx={{
                      bgcolor: mergedView ? UI.color.accent : UI.color.bgHover,
                      color: mergedView ? '#fff' : UI.color.textSub,
                      '&:hover': { bgcolor: mergedView ? '#1d4ed8' : UI.color.border },
                      width: 32, height: 32, fontSize: 14,
                    }}
                  >
                    ⇅
                  </IconButton>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9, mt: 0.3 }}>
                    {mergedView ? '統合中' : '統合'}
                  </Typography>
                </Box>
              </Tooltip>
            )}
            <Tooltip title={compactMode ? '全件表示に戻す' : 'コンパクト表示'}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <IconButton
                  size="small"
                  onClick={() => {
                    const next = !compactMode;
                    setCompactMode(next);
                    document.cookie = `daylist_compact=${next}; path=/; max-age=${60 * 60 * 24 * 365}`;
                  }}
                  sx={{
                    bgcolor: compactMode ? UI.color.textSub : UI.color.bgHover,
                    color: compactMode ? '#fff' : UI.color.textSub,
                    '&:hover': { bgcolor: compactMode ? UI.color.text : UI.color.border },
                    width: 32, height: 32, fontSize: 14,
                  }}
                >
                  {compactMode ? '⊟' : '⊞'}
                </IconButton>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9, mt: 0.3 }}>
                  {compactMode ? 'compact' : ''}
                </Typography>
              </Box>
            </Tooltip>
            {isFlightFeatureAvailable && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <IconButton
                  size="small"
                  onClick={(e) => setFlightMenuAnchor(e.currentTarget)}
                  disabled={flightLoading}
                  sx={{
                    bgcolor: Object.keys(flightInfoMap).length > 0 ? '#0ea5e9' : UI.color.bgHover,
                    color: Object.keys(flightInfoMap).length > 0 ? '#fff' : UI.color.textSub,
                    '&:hover': { bgcolor: Object.keys(flightInfoMap).length > 0 ? '#0284c7' : UI.color.border },
                    width: 32, height: 32, fontSize: 15,
                  }}
                >
                  ✈
                </IconButton>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9, mt: 0.3 }}>
                  {flightLoading ? '取得中…' : (flightData ? `FIS ${String(flightData.fetchedAt).slice(11, 16)}` : 'フライト')}

                </Typography>
              </Box>
            )}
            <Menu
              anchorEl={flightMenuAnchor}
              open={!!flightMenuAnchor}
              onClose={() => setFlightMenuAnchor(null)}
            >
              <MenuItem onClick={handleFlightFixOpen}>フライト表示を修正する</MenuItem>
              <MenuItem onClick={handleFlightUpdate}>フライト情報を更新する</MenuItem>
            </Menu>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <IconButton
                color="primary"
                onClick={handleManualRefresh}
                size="small"
                sx={{
                  bgcolor: 'primary.main', color: '#fff',
                  '&:hover': { bgcolor: 'primary.dark' },
                  width: 32, height: 32,
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, mt: 0.3 }}>
                {lastUpdated}
              </Typography>
            </Box>
          </Box>
          </Box>

          {/* 右側: ダイジェスト + 実況ウィンドウ（2026-07-26）。
              PC はインライン常駐、スマホはダイジェストのみボタン+ポップアップ。
              実況は 2026-07-26 に本番リリース(ver.0)。 */}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'stretch' }}>
            <DigestWindow lineUsers={displayLineUsers} vehicleStatusList={vehicleStatusList} inline={!isMobile} onOpenLineChat={handleOpenLineChat} />
            {/* 実況は features.liveReport で出し分ける（2026-08-01 に店舗別化）。
                以前は LINE フラグで塞いでいたが、サーバ側を店舗別にしたので
                宮古でも自店の保留中リクエストから生成できる。
                詳細は LiveWindow.tsx 冒頭のコメント参照 */}
            {!isMobile && isLiveReportEnabled() && (
              <LiveWindow lineUsers={displayLineUsers} reservationStatusList={reservationStatusList} vehicleStatusList={vehicleStatusList} />
            )}
          </Box>
        </Box>
        {/* スマホ時: タブも sticky ブロック内 */}
        {isMobile && !loading && (
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              minHeight: 40,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Tab label="貸出" sx={{ flex: 2, minWidth: 0, fontSize: 13, fontWeight: 700, px: 0, minHeight: 40, py: 0.5 }} />
            <Tab label="返却" sx={{ flex: 2, minWidth: 0, fontSize: 13, fontWeight: 700, px: 0, minHeight: 40, py: 0.5 }} />
            <Tab icon={<SearchIcon sx={{ fontSize: 18 }} />} sx={{ flex: 1, minWidth: 0, px: 0, minHeight: 40, py: 0.5 }} />
            <Tab icon={<SettingsIcon sx={{ fontSize: 18 }} />} sx={{ flex: 1, minWidth: 0, px: 0, minHeight: 40, py: 0.5 }} />
          </Tabs>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {isMobile ? (
            <Box>
              <Box role="tabpanel" hidden={tabValue !== 0}>
                {tabValue === 0 && (
                  <ReservationTable
                    key={`dep-${settingsVersion}`}
                    hideToolbar
                    compactMode={compactMode}
                    isSimpleView={isMobile}
                    isToday={selectedDate === dayjs().format('YYYY-MM-DD')}
                    title="貸出"
                    data={finalDepartures}
                    type="departure"
                    vehicles={vehicles}
                    formatTime={formatTime}
                    onVehicleClick={handleVehicleClick}
                    onStatusClick={handleStatusClick}
                    onLocationStatusClick={handleLocationStatusClick}
                    onReservationSave={handleReservationSave}
                    onQuickUpdate={handleQuickUpdate}
                    onStaffClick={handleStaffClick}
                    onContactClick={(r: any, t: any) => setContactModalReservation({ reservation: r, rowType: t })}
                    onLicenseToggle={handleLicenseToggle}
                    onCommunicationToolsUpdate={handleCommunicationToolsUpdate}
                    squarePaymentCache={squarePaymentCache}
                    squarePaymentLoading={squarePaymentLoading}
                    onPaymentCheck={handlePaymentCheck}
                    onLocateVehicle={handleLocateVehicle}
                    openReservationRequest={openResRequest}
                    onBeforeOpenReservation={handleBeforeOpenReservation}
                    forceCloseReservationPanel={!!selectedVehicle}
                    reservationStatusList={reservationStatusList}
                    vehicleStatusList={vehicleStatusList}
                    upcomingStatuses={upcomingStatuses}
                    onCheckUpdate={handleCheckUpdate}
                    onAssignClick={handleOpenAssignModal}
                    carTypesList={carTypesList}
                    onHoverVehicle={setHoveredVehicleId}
                    onLeaveVehicle={() => setHoveredVehicleId(null)}
                    lineUsers={displayLineUsers}
                    onOpenLineChat={handleOpenLineChat}
                    onOpenResvChat={resvChatEnabled ? handleOpenResvChat : null}
                    onVehicleSwap={handleVehicleSwapRequest}
                    vehicleDragSource={vehicleDragSource}
                    onVehicleDragSourceChange={setVehicleDragSource}
                    staffList={staffList}
                    locationStatusList={locationStatusList}
                    externalSearchTerm=""
                    onSettingsOpen={() => {}}
                  />
                )}
              </Box>
              <Box role="tabpanel" hidden={tabValue !== 1}>
                {tabValue === 1 && (
                  <ReservationTable
                    key={`ret-${settingsVersion}`}
                    hideToolbar
                    compactMode={compactMode}
                    isSimpleView={isMobile}
                    isToday={selectedDate === dayjs().format('YYYY-MM-DD')}
                    title="返却"
                    data={finalReturns}
                    type="return"
                    vehicles={vehicles}
                    formatTime={formatTime}
                    onVehicleClick={handleVehicleClick}
                    onStatusClick={handleStatusClick}
                    onLocationStatusClick={handleLocationStatusClick}
                    onReservationSave={handleReservationSave}
                    onQuickUpdate={handleQuickUpdate}
                    onStaffClick={handleStaffClick}
                    onContactClick={(r: any, t: any) => setContactModalReservation({ reservation: r, rowType: t })}
                    onLicenseToggle={handleLicenseToggle}
                    onCommunicationToolsUpdate={handleCommunicationToolsUpdate}
                    squarePaymentCache={squarePaymentCache}
                    squarePaymentLoading={squarePaymentLoading}
                    onPaymentCheck={handlePaymentCheck}
                    onLocateVehicle={handleLocateVehicle}
                    openReservationRequest={openResRequest}
                    onBeforeOpenReservation={handleBeforeOpenReservation}
                    forceCloseReservationPanel={!!selectedVehicle}
                    reservationStatusList={reservationStatusList}
                    vehicleStatusList={vehicleStatusList}
                    upcomingStatuses={upcomingStatuses}
                    onCheckUpdate={handleCheckUpdate}
                    onAssignClick={handleOpenAssignModal}
                    carTypesList={carTypesList}
                    onHoverVehicle={setHoveredVehicleId}
                    onLeaveVehicle={() => setHoveredVehicleId(null)}
                    lineUsers={displayLineUsers}
                    onOpenLineChat={handleOpenLineChat}
                    onOpenResvChat={resvChatEnabled ? handleOpenResvChat : null}
                    onVehicleSwap={handleVehicleSwapRequest}
                    vehicleDragSource={vehicleDragSource}
                    onVehicleDragSourceChange={setVehicleDragSource}
                    staffList={staffList}
                    locationStatusList={locationStatusList}
                    externalSearchTerm=""
                    onSettingsOpen={() => {}}
                  />
                )}
              </Box>
              {/* 検索タブ */}
              <Box role="tabpanel" hidden={tabValue !== 2}>
                {tabValue === 2 && (
                  <Box sx={{ p: 2, bgcolor: '#fff', position: 'relative', zIndex: 1, minHeight: '100vh' }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="顧客名・車両コード・予約コードで検索"
                      value={mobileSearchTerm}
                      onChange={(e) => setMobileSearchTerm(e.target.value)}
                      autoFocus
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon color="action" fontSize="small" /></InputAdornment>,
                        endAdornment: mobileSearchTerm ? <InputAdornment position="end"><IconButton size="small" onClick={() => setMobileSearchTerm('')}><CloseIcon fontSize="small" /></IconButton></InputAdornment> : null,
                      }}
                      sx={{ mb: 2 }}
                    />
                    {mobileSearchTerm.trim() ? (
                      <>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>貸出 ({finalDepartures.filter(r => {
                          const q = mobileSearchTerm.toLowerCase();
                          return (r.customer_name || '').toLowerCase().includes(q) || (r.vehicle?.vehicle_code || '').toLowerCase().includes(q) || (r.contact_code || '').toLowerCase().includes(q);
                        }).length})</Typography>
                        <ReservationTable
                          key={`search-dep-${settingsVersion}`}
                          hideToolbar
                          compactMode
                          isSimpleView={isMobile}
                          isToday={selectedDate === dayjs().format('YYYY-MM-DD')}
                          title="貸出"
                          data={finalDepartures}
                          type="departure"
                          vehicles={vehicles}
                          formatTime={formatTime}
                          onVehicleClick={handleVehicleClick}
                          onStatusClick={handleStatusClick}
                          onLocationStatusClick={handleLocationStatusClick}
                          onReservationSave={handleReservationSave}
                          onQuickUpdate={handleQuickUpdate}
                          onStaffClick={handleStaffClick}
                    onContactClick={(r: any, t: any) => setContactModalReservation({ reservation: r, rowType: t })}
                          onLicenseToggle={handleLicenseToggle}
                          onCommunicationToolsUpdate={handleCommunicationToolsUpdate}
                          squarePaymentCache={squarePaymentCache}
                          squarePaymentLoading={squarePaymentLoading}
                          onPaymentCheck={handlePaymentCheck}
                    onLocateVehicle={handleLocateVehicle}
                    openReservationRequest={openResRequest}
                          onBeforeOpenReservation={handleBeforeOpenReservation}
                          forceCloseReservationPanel={!!selectedVehicle}
                          reservationStatusList={reservationStatusList}
                          vehicleStatusList={vehicleStatusList}
                          upcomingStatuses={upcomingStatuses}
                          onCheckUpdate={handleCheckUpdate}
                          onAssignClick={handleOpenAssignModal}
                          carTypesList={carTypesList}
                          lineUsers={displayLineUsers}
                          onOpenLineChat={handleOpenLineChat}
                          onOpenResvChat={resvChatEnabled ? handleOpenResvChat : null}
                    onVehicleSwap={handleVehicleSwapRequest}
                    vehicleDragSource={vehicleDragSource}
                    onVehicleDragSourceChange={setVehicleDragSource}
                          staffList={staffList}
                          locationStatusList={locationStatusList}
                          externalSearchTerm={mobileSearchTerm}
                          onHoverVehicle={setHoveredVehicleId}
                          onLeaveVehicle={() => setHoveredVehicleId(null)}
                          onSettingsOpen={() => {}}
                        />
                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 700 }}>返却 ({finalReturns.filter(r => {
                          const q = mobileSearchTerm.toLowerCase();
                          return (r.customer_name || '').toLowerCase().includes(q) || (r.vehicle?.vehicle_code || '').toLowerCase().includes(q) || (r.contact_code || '').toLowerCase().includes(q);
                        }).length})</Typography>
                        <ReservationTable
                          key={`search-ret-${settingsVersion}`}
                          hideToolbar
                          compactMode
                          isSimpleView={isMobile}
                          isToday={selectedDate === dayjs().format('YYYY-MM-DD')}
                          title="返却"
                          data={finalReturns}
                          type="return"
                          vehicles={vehicles}
                          formatTime={formatTime}
                          onVehicleClick={handleVehicleClick}
                          onStatusClick={handleStatusClick}
                          onLocationStatusClick={handleLocationStatusClick}
                          onReservationSave={handleReservationSave}
                          onQuickUpdate={handleQuickUpdate}
                          onStaffClick={handleStaffClick}
                    onContactClick={(r: any, t: any) => setContactModalReservation({ reservation: r, rowType: t })}
                          onLicenseToggle={handleLicenseToggle}
                          onCommunicationToolsUpdate={handleCommunicationToolsUpdate}
                          squarePaymentCache={squarePaymentCache}
                          squarePaymentLoading={squarePaymentLoading}
                          onPaymentCheck={handlePaymentCheck}
                    onLocateVehicle={handleLocateVehicle}
                    openReservationRequest={openResRequest}
                          onBeforeOpenReservation={handleBeforeOpenReservation}
                          forceCloseReservationPanel={!!selectedVehicle}
                          reservationStatusList={reservationStatusList}
                          vehicleStatusList={vehicleStatusList}
                          upcomingStatuses={upcomingStatuses}
                          onCheckUpdate={handleCheckUpdate}
                          onAssignClick={handleOpenAssignModal}
                          carTypesList={carTypesList}
                          lineUsers={displayLineUsers}
                          onOpenLineChat={handleOpenLineChat}
                          onOpenResvChat={resvChatEnabled ? handleOpenResvChat : null}
                    onVehicleSwap={handleVehicleSwapRequest}
                    vehicleDragSource={vehicleDragSource}
                    onVehicleDragSourceChange={setVehicleDragSource}
                          staffList={staffList}
                          locationStatusList={locationStatusList}
                          externalSearchTerm={mobileSearchTerm}
                          onHoverVehicle={setHoveredVehicleId}
                          onLeaveVehicle={() => setHoveredVehicleId(null)}
                          onSettingsOpen={() => {}}
                        />
                      </>
                    ) : (
                      <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                        検索語を入力してください
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
              {/* 設定タブ */}
              <Box role="tabpanel" hidden={tabValue !== 3}>
                {tabValue === 3 && (
                  <Box sx={{ p: 2, bgcolor: '#fff', position: 'relative', zIndex: 1, minHeight: '100vh' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>表示設定</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2">コンパクト表示</Typography>
                        <Box
                          onClick={() => {
                            const next = !compactMode;
                            setCompactMode(next);
                            document.cookie = `daylist_compact=${next}; path=/; max-age=${60 * 60 * 24 * 365}`;
                          }}
                          sx={{ width: 44, height: 24, borderRadius: 12, cursor: 'pointer', position: 'relative', bgcolor: compactMode ? '#3b82f6' : '#94a3b8', transition: 'background 0.2s' }}
                        >
                          <Box sx={{ position: 'absolute', top: 2, left: compactMode ? 22 : 2, width: 20, height: 20, borderRadius: '50%', bgcolor: '#fff', transition: 'left 0.2s' }} />
                        </Box>
                      </Box>
                    </Box>

                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1, color: '#475569' }}>表示項目</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {MOBILE_FIELD_OPTIONS.map(field => (
                        <Box
                          key={field.id}
                          onClick={() => handleMobileFieldToggle(field.id)}
                          sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            py: 1, px: 1, borderRadius: 1, cursor: 'pointer',
                            '&:hover': { bgcolor: '#f1f5f9' },
                          }}
                        >
                          <Typography variant="body2">{field.label}</Typography>
                          <Box sx={{
                            width: 44, height: 24, borderRadius: 12, position: 'relative',
                            bgcolor: mobileFieldVisibility[field.id] ? '#3b82f6' : '#94a3b8',
                            transition: 'background 0.2s',
                          }}>
                            <Box sx={{
                              position: 'absolute', top: 2,
                              left: mobileFieldVisibility[field.id] ? 22 : 2,
                              width: 20, height: 20, borderRadius: '50%', bgcolor: '#fff',
                              transition: 'left 0.2s',
                            }} />
                          </Box>
                        </Box>
                      ))}
                    </Box>

                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1, color: '#475569' }}>予約ステータス</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {(reservationStatusList || []).map((status: any) => {
                        const isOn = mobileStatusFilter[String(status.id)] !== false;
                        return (
                          <Box
                            key={status.id}
                            onClick={() => handleMobileStatusToggle(status.id)}
                            sx={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              py: 1, px: 1, borderRadius: 1, cursor: 'pointer',
                              '&:hover': { bgcolor: '#f1f5f9' },
                            }}
                          >
                            <Typography variant="body2">{status.label}</Typography>
                            <Box sx={{
                              width: 44, height: 24, borderRadius: 12, position: 'relative',
                              bgcolor: isOn ? '#3b82f6' : '#94a3b8',
                              transition: 'background 0.2s',
                            }}>
                              <Box sx={{
                                position: 'absolute', top: 2,
                                left: isOn ? 22 : 2,
                                width: 20, height: 20, borderRadius: '50%', bgcolor: '#fff',
                                transition: 'left 0.2s',
                              }} />
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          ) : mergedView ? (
            /* 統合リスト表示: 貸出+返却を時間順の1リストで（30分グリッド + レーン + ペア連結） */
            <Box sx={{ maxWidth: 980, minWidth: 580, mx: 'auto' }}>
              <ReservationTable
                compactMode={compactMode}
                isSimpleView={false}
                isToday={selectedDate === dayjs().format('YYYY-MM-DD')}
                title="貸出・返却（統合）"
                data={mergedListData}
                type="departure"
                mergedView
                vehicles={vehicles}
                formatTime={formatTime}
                onVehicleClick={handleVehicleClick}
                onStatusClick={handleStatusClick}
                onLocationStatusClick={handleLocationStatusClick}
                onReservationSave={handleReservationSave}
                onQuickUpdate={handleQuickUpdate}
                onStaffClick={handleStaffClick}
                onContactClick={(r: any, t: any) => setContactModalReservation({ reservation: r, rowType: t })}
                onLicenseToggle={handleLicenseToggle}
                onCommunicationToolsUpdate={handleCommunicationToolsUpdate}
                squarePaymentCache={squarePaymentCache}
                squarePaymentLoading={squarePaymentLoading}
                onPaymentCheck={handlePaymentCheck}
                    onLocateVehicle={handleLocateVehicle}
                    openReservationRequest={openResRequest}
                onBeforeOpenReservation={handleBeforeOpenReservation}
                forceCloseReservationPanel={!!selectedVehicle}
                reservationStatusList={reservationStatusList}
                vehicleStatusList={vehicleStatusList}
                upcomingStatuses={upcomingStatuses}
                locationStatusList={locationStatusList}
                staffList={staffList}
                onCheckUpdate={handleCheckUpdate}
                onAssignClick={handleOpenAssignModal}
                carTypesList={carTypesList}
                allowCopy={false}
                onHoverVehicle={setHoveredVehicleId}
                onLeaveVehicle={() => setHoveredVehicleId(null)}
                lineUsers={displayLineUsers}
                onOpenLineChat={handleOpenLineChat}
                onOpenResvChat={resvChatEnabled ? handleOpenResvChat : null}
                onVehicleSwap={handleVehicleSwapRequest}
                vehicleDragSource={vehicleDragSource}
                onVehicleDragSourceChange={setVehicleDragSource}
                externalSearchTerm={undefined}
                onSettingsOpen={() => {}}
                flightInfoMap={flightInfoMap}
              />
            </Box>
          ) : (
            <Box sx={{
              display: 'grid',
              // 各リストに最低幅を確保し、行内要素の潜り込み（列の重なり）を防ぐ。
              // 画面が足りない時はページ側（ルートの minWidth: fit-content）で横スクロールさせる（2026-08-10）
              // 2026-08-10: 行を2段化して必要幅が約530pxまで減ったため 720px → 560px に縮小。
              // 右側に無駄な余白が出ないよう、以降は 1fr の可変領域に任せる
              gridTemplateColumns: 'repeat(2, minmax(560px, 1fr))',
              gap: 2
            }}>
              <Box sx={{ minWidth: 0 }}>
                <ReservationTable
                  compactMode={compactMode}
                  isSimpleView={isMobile}
                  isToday={selectedDate === dayjs().format('YYYY-MM-DD')}
                  title="貸出"
                  data={finalDepartures}
                  type="departure"
                  vehicles={vehicles}
                  formatTime={formatTime}
                  onVehicleClick={handleVehicleClick}
                  onStatusClick={handleStatusClick}
                  onLocationStatusClick={handleLocationStatusClick}
                  onReservationSave={handleReservationSave}
                  onQuickUpdate={handleQuickUpdate}
                  onStaffClick={handleStaffClick}
                    onContactClick={(r: any, t: any) => setContactModalReservation({ reservation: r, rowType: t })}
                  onLicenseToggle={handleLicenseToggle}
                  onCommunicationToolsUpdate={handleCommunicationToolsUpdate}
                  squarePaymentCache={squarePaymentCache}
                  squarePaymentLoading={squarePaymentLoading}
                  onPaymentCheck={handlePaymentCheck}
                    onLocateVehicle={handleLocateVehicle}
                    openReservationRequest={openResRequest}
                  onBeforeOpenReservation={handleBeforeOpenReservation}
                  forceCloseReservationPanel={!!selectedVehicle}
                  reservationStatusList={reservationStatusList}
                  vehicleStatusList={vehicleStatusList}
                  upcomingStatuses={upcomingStatuses}
                  locationStatusList={locationStatusList}
                  staffList={staffList}
                  onCheckUpdate={handleCheckUpdate}
                  onAssignClick={handleOpenAssignModal}
                  carTypesList={carTypesList}
                  allowCopy={false}
                  onHoverVehicle={setHoveredVehicleId}
                  onLeaveVehicle={() => setHoveredVehicleId(null)}
                  lineUsers={displayLineUsers}
                  onOpenLineChat={handleOpenLineChat}
                  onOpenResvChat={resvChatEnabled ? handleOpenResvChat : null}
                  onVehicleSwap={handleVehicleSwapRequest}
                  vehicleDragSource={vehicleDragSource}
                  onVehicleDragSourceChange={setVehicleDragSource}
                  externalSearchTerm={undefined}
                  onSettingsOpen={() => {}}
                  flightInfoMap={flightInfoMap}
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <ReservationTable
                  compactMode={compactMode}
                  isSimpleView={isMobile}
                  isToday={selectedDate === dayjs().format('YYYY-MM-DD')}
                  title="返却"
                  data={finalReturns}
                  type="return"
                  vehicles={vehicles}
                  formatTime={formatTime}
                  onVehicleClick={handleVehicleClick}
                  onStatusClick={handleStatusClick}
                  onLocationStatusClick={handleLocationStatusClick}
                  onReservationSave={handleReservationSave}
                  onQuickUpdate={handleQuickUpdate}
                  onStaffClick={handleStaffClick}
                    onContactClick={(r: any, t: any) => setContactModalReservation({ reservation: r, rowType: t })}
                  onLicenseToggle={handleLicenseToggle}
                  onCommunicationToolsUpdate={handleCommunicationToolsUpdate}
                  squarePaymentCache={squarePaymentCache}
                  squarePaymentLoading={squarePaymentLoading}
                  onPaymentCheck={handlePaymentCheck}
                    onLocateVehicle={handleLocateVehicle}
                    openReservationRequest={openResRequest}
                  onBeforeOpenReservation={handleBeforeOpenReservation}
                  forceCloseReservationPanel={!!selectedVehicle}
                  reservationStatusList={reservationStatusList}
                  vehicleStatusList={vehicleStatusList}
                  upcomingStatuses={upcomingStatuses}
                  locationStatusList={locationStatusList}
                  staffList={staffList}
                  onCheckUpdate={handleCheckUpdate}
                  onAssignClick={handleOpenAssignModal}
                  carTypesList={carTypesList}
                  allowCopy={false}
                  onHoverVehicle={setHoveredVehicleId}
                  onLeaveVehicle={() => setHoveredVehicleId(null)}
                  lineUsers={displayLineUsers}
                  onOpenLineChat={handleOpenLineChat}
                  onOpenResvChat={resvChatEnabled ? handleOpenResvChat : null}
                  onVehicleSwap={handleVehicleSwapRequest}
                  vehicleDragSource={vehicleDragSource}
                  onVehicleDragSourceChange={setVehicleDragSource}
                  externalSearchTerm={undefined}
                  onSettingsOpen={() => {}}
                />
              </Box>
            </Box>
          )}

          {/* コンパクト表示トグル */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 1 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                const next = !compactMode;
                setCompactMode(next);
                document.cookie = `daylist_compact=${next}; path=/; max-age=${60 * 60 * 24 * 365}`;
              }}
              sx={{ fontSize: 11, px: 2, py: 0.5, borderRadius: 4, color: UI.color.textSub, bgcolor: UI.color.bgHover, '&:hover': { bgcolor: UI.color.border } }}
            >
              {compactMode ? '全件表示に戻す' : 'コンパクト表示'}
            </Button>
          </Box>

          {/* 車両ポジションマップ */}
          <Box ref={vehiclePositionRef} sx={{ mt: 5, mb: 4, pt: 3, borderTop: `1px solid ${UI.color.border}` }}>
            <Typography sx={{ mb: 2, fontSize: UI.font.lg, fontWeight: 700, color: UI.color.text }}>
              車両ポジション
            </Typography>
            <VehiclePositionMap
              vehicles={vehicles}
              vehicleStatusList={vehicleStatusList}
              locationStatusList={locationStatusList}
              departures={finalDepartures}
              returns={finalReturns}
              upcomingStatuses={upcomingStatuses}
              onVehicleClick={(v) => handleVehicleClick(v, null)}
              onStaffClick={handleStaffClick}
              onReservationClick={handleOpenReservationPanel}
              onOpenLineChat={handleOpenLineChat}
              lineUsers={displayLineUsers}
              staffList={staffList}
              userId={user?.id}
              locateVehicleId={locateVehicle.id}
              locateNonce={locateVehicle.nonce}
            />
          </Box>

          {/* カスタムロケーション セクション */}
          <Box sx={{ mt: 5, mb: 4, pt: 3, borderTop: `1px solid ${UI.color.border}` }}>
            <Typography sx={{ mb: 2, fontSize: UI.font.lg, fontWeight: 700, color: UI.color.text }}>
              車両位置
            </Typography>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Tabs
                value={bottomTabValue}
                onChange={handleBottomTabChange}
                aria-label="location tabs"
                textColor="primary"
                indicatorColor="primary"
                sx={{ flexGrow: 1 }}
              >
                {bottomTabConfigs.map((config, index) => (
                  <Tab
                    key={index}
                    label={config.name || `タブ${index + 1}`}
                    sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                  />
                ))}
              </Tabs>
              <Tooltip title="タブの絞り込み設定">
                <IconButton onClick={handleOpenTabConfig} color="primary" sx={{ ml: 2 }}>
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {locationGroupVehicles.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', px: 2 }}>
                現在、{bottomTabConfigs[bottomTabValue]?.name || '対象'}に配置されている車両はありません。
              </Typography>
            ) : (
              <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#fff' }}>
                {locationGroupVehicles.map((v, idx) => {
                  const upcoming = upcomingStatuses[v.id];
                  const nextRes = upcoming?.next;
                  return (
                    <Box
                      key={v.id}
                      onClick={() => handleVehicleClick(v, null)}
                      sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: idx === locationGroupVehicles.length - 1 ? 'none' : '1px solid #eee',
                        transition: 'background-color 0.2s',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#f8fafc'
                        }
                      }}
                    >
                      <Typography sx={{ fontWeight: 'bold', color: '#334155' }}>
                        {v.vehicle_code}
                      </Typography>
                      <Box sx={{ textAlign: 'right' }}>
                        {nextRes ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              次回予約:
                            </Typography>
                            <Typography variant="caption" sx={{
                              fontWeight: 'bold',
                              color: upcoming.color
                            }}>
                              {dayjs(nextRes.start_date).format('M/D HH:mm')}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            次回予約なし
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Paper>
            )}
          </Box>
        </>
      )}

      <VehicleDetailPanel
        open={Boolean(selectedVehicle)}
        vehicle={selectedVehicle}
        onClose={handleClosePanel}
        onSave={handleSaveVehicle}
        onDirtyChange={setVehiclePanelDirty}
        vehicleStatusList={vehicleStatusList}
        locationStatusList={locationStatusList}
        onAssignClick={selectedVehicleReservation ? () => handleOpenAssignModal(selectedVehicleReservation) : null}
      />

      {/* フライト表示修正ダイアログ（空港の到着便と自動照合できなかった予約の表記ゆらぎを修正） */}
      <Dialog open={flightFixOpen} onClose={() => setFlightFixOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          フライト表示の修正（貸出リスト・到着便）
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.3 }}>
            {isMiyakoInstance ? '宮古空港' : '那覇空港'}の到着便と自動照合できなかった予約の表記を修正します。
            {flightData && ` 取得: ${flightData.fetchedAt} / 当日到着便 ${flightData.arrivals.length}件`}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {flightFixRows.length === 0 ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              すべての到着便表記がフライト情報と照合できています。修正は不要です。
            </Alert>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleRunGemini}
                  disabled={flightGeminiLoading || flightFixRows.every(r => r.applied)}
                >
                  {flightGeminiLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                  {flightGeminiLoading ? 'Gemini照合中…' : 'Geminiで修正案を生成'}
                </Button>
              </Box>
              <TableContainer sx={{ border: `1px solid ${UI.color.border}`, borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>予約</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>予約情報の表記</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>URLのフライト情報の表記</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Gemini修正案（編集可）</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {flightFixRows.map((r) => (
                      <TableRow key={r.id} sx={{ backgroundColor: r.applied ? '#f0fdf4' : 'inherit' }}>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>{r.name}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{r.text}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {r.fisText || <Typography component="span" sx={{ color: 'text.disabled', fontSize: '0.8rem' }}>-</Typography>}
                          {r.reason && (
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>{r.reason}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={r.suggestion}
                            onChange={(e) => {
                              const v = e.target.value;
                              setFlightFixRows(prev => prev.map(x => x.id === r.id ? { ...x, suggestion: v } : x));
                            }}
                            placeholder="例: NH471"
                            disabled={r.applied}
                            sx={{ width: 130 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="contained"
                            size="small"
                            disabled={!String(r.suggestion || '').trim() || r.applied || r.saving}
                            onClick={() => handleApplyFlightFix(r)}
                          >
                            {r.saving ? '保存中…' : (r.applied ? '適用済' : '適用')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                適用すると予約の到着便（arrival_flight）を書き換え、変更履歴を予約メモに記録します。
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFlightFixOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* タブ設定ダイアログ */}
      <Dialog open={tabConfigDialogOpen} onClose={() => setTabConfigDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>下部タブの絞り込み設定</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3, fontSize: '0.85rem' }}>
            各タブの名称と、表示したいステータスを複数選択できます。
            選択したステータスのいずれかが車両の状態や本日の予約に該当する場合、その車両が表示されます。
          </DialogContentText>
          <Stack spacing={4}>
            {editingTabConfigs.map((config, index) => {
              return (
                <Box key={index} sx={{ p: 2, border: '1px solid #eee', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                    タブ {index + 1} の設定
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="タブ名"
                      fullWidth
                      size="small"
                      value={config.name || ''}
                      onChange={(e) => handleTabConfigChange(index, 'name', e.target.value)}
                      placeholder="例: 空港店"
                    />
                    <FormControl fullWidth size="small">
                      <InputLabel id={`tab-select-label-${index}`}>表示するステータス（複数可）</InputLabel>
                      <Select
                        labelId={`tab-select-label-${index}`}
                        multiple
                        value={config.selectedLabels || []}
                        onChange={(e) => handleTabConfigChange(index, 'selectedLabels', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                        input={<OutlinedInput label="表示するステータス（複数可）" />}
                        renderValue={(selected) => Array.isArray(selected) ? selected.join(', ') : ''}
                      >
                        {[
                          { category: "車両", items: vehicleStatusList },
                          { category: "移動", items: locationStatusList.filter(o => o.id !== null) },
                          { category: "予約", items: reservationStatusList }
                        ].map((cat) => [
                          <MenuItem key={`header-${cat.category}`} disabled sx={{ fontWeight: "bold", opacity: "1 !important", color: "text.secondary", fontSize: "0.75rem" }}>
                            {cat.category}
                          </MenuItem>,
                          ...cat.items.map((opt) => (
                            <MenuItem key={`${cat.category}-${opt.id}`} value={opt.label}>
                              <Checkbox checked={(config.selectedLabels || []).indexOf(opt.label) > -1} size="small" />
                              <ListItemText primary={opt.label} />
                            </MenuItem>
                          ))
                        ])}
                      </Select>
                    </FormControl>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.includeArchived === true}
                          onChange={(e) => handleTabConfigChange(index, 'includeArchived', e.target.checked)}
                          size="small"
                          color="warning"
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                          アーカイブ済みの車両も含める（active=OFF / 使用停止）
                        </Typography>
                      }
                    />
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setTabConfigDialogOpen(false)} color="inherit" size="small">キャンセル</Button>
          <Button onClick={handleSaveTabConfig} variant="contained" color="primary" size="small">保存</Button>
        </DialogActions>
      </Dialog>

      {/* 未保存確認ダイアログ */}
      {unsavedConfirm && (
        <div>
          <div
            onClick={() => handleUnsavedConfirmAction('cancel')}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.4)',
              zIndex: 3900
            }}
          />
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 4000,
              background: '#fff',
              padding: 16,
              borderRadius: 8,
              border: '1px solid #ccc',
              boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
              minWidth: 360,
              maxWidth: 440
            }}
          >
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                未保存の情報がありますが{(unsavedConfirm.targetVehicle || unsavedConfirm.closeVehiclePanel) ? '新しいパネルを開きますか？' : '閉じますか？'}
              </div>
              <div style={{ fontSize: 14, color: '#64748b' }}>
                未保存の変更があります。
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="outlined" onClick={() => handleUnsavedConfirmAction('cancel')}>
                いいえ
              </Button>
              <Button variant="contained" onClick={() => handleUnsavedConfirmAction('discard')}>
                破棄して{(unsavedConfirm.targetVehicle || unsavedConfirm.closeVehiclePanel) ? '開く' : '閉じる'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={statusModal.open} onClose={() => setStatusModal({ ...statusModal, open: false })}>
        <DialogTitle>ステータス変更</DialogTitle>
        <DialogContent>
          <Grid {...{container: true} as any} spacing={1} sx={{ mt: 1 }}>
            {vehicleStatusList.map((status) => (
              <Grid {...{item: true, xs: 4} as any} key={status.id}>
                <Button
                  variant="contained"
                  color={(status.color || 'primary') as any}
                  fullWidth
                  onClick={() => handleStatusUpdate(status.id)}
                  sx={{
                    height: '100%',
                    ...(status.bg_color && {
                      backgroundColor: status.bg_color,
                      '&:hover': {
                        backgroundColor: status.bg_color,
                        filter: 'brightness(0.9)',
                      }
                    }),
                    ...(status.text_color && {
                      color: status.text_color
                    })
                  }}
                >
                  {status.label}
                </Button>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>

      {/* 行き先（ゾーン）選択モーダル（2026-08-25）。
          迎え中/送り中 は行き先が複数あり、どこへ行くかで車両ステータス自体も変わる
          （赤嶺駅なら 赤嶺駅P.U）。選ぶと移動ステータスも一緒に入り、続けて
          運転者モーダルが出る = 車両ポジションで同じエリアへ DnD したときと同じ流れ */}
      <Dialog
        open={moveTargetModal.open}
        onClose={() => setMoveTargetModal({ open: false, vehicle: null, reservation: null, choices: [] })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 0.5, fontSize: '1rem' }}>
          どこへ向かいますか？
          {moveTargetModal.vehicle && (
            <Typography variant="body2" color="text.secondary">
              {moveTargetModal.vehicle.vehicle_code}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid {...{container: true} as any} spacing={1} sx={{ mt: 0.5 }}>
            {moveTargetModal.choices.map((plan: any) => {
              const locLabel = locationStatusList.find((l: any) => l.code && l.code === plan.locationStatusCode)?.label;
              return (
                <Grid {...{item: true, xs: 6} as any} key={plan.zoneId}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleMoveTargetSelect(plan)}
                    sx={{ height: '100%', minHeight: '52px', fontSize: '0.8rem', lineHeight: 1.4 }}
                  >
                    <span>
                      {plan.label}
                      {locLabel && (
                        <Typography component="span" sx={{ display: 'block', fontSize: '0.65rem', opacity: 0.7 }}>
                          {locLabel}
                        </Typography>
                      )}
                    </span>
                  </Button>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveTargetModal({ open: false, vehicle: null, reservation: null, choices: [] })}>
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={locationStatusModal.open} onClose={() => setLocationStatusModal({ ...locationStatusModal, open: false })}>
        <DialogTitle>移動ステータス変更</DialogTitle>
        <DialogContent>
          <Grid {...{container: true} as any} spacing={1} sx={{ mt: 1 }}>
            {getLocationStatusOptions(locationStatusModal.listType).map((status) => (
              <Grid {...{item: true, xs: 4} as any} key={status.id ?? 'null'}>
                <Button
                  variant={status.bg_color ? "contained" : "outlined"}
                  fullWidth
                  onClick={() => handleLocationStatusUpdate(status.id)}
                  sx={{
                    height: '100%',
                    fontSize: '0.75rem',
                    minHeight: '48px',
                    ...(status.bg_color && {
                      backgroundColor: status.bg_color,
                      color: status.text_color || '#fff',
                      borderColor: status.bg_color,
                      '&:hover': {
                        backgroundColor: `${status.bg_color}dd`,
                        filter: 'brightness(0.9)',
                      }
                    }),
                    ...(!status.bg_color && {
                      color: status.text_color || undefined,
                      borderColor: status.text_color || undefined
                    })
                  }}
                >
                  {status.label}
                </Button>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>

      {/* 連絡モーダル。
          貸出行 = 到着連絡 + ピックアップサブゾーン（ロビー/14番/…）
          返却行 = 返却連絡 + 返却の受け方（通常/赤嶺駅/送迎車/返却車/返却無）
          同じ予約が貸出日と返却日の両方でリストに出るため、保存キーを分けている（2026-08-24） */}
      <Dialog open={!!contactModalReservation} onClose={() => setContactModalReservation(null)} maxWidth="xs" fullWidth>
        {contactModalReservation && (() => {
          const r = contactModalReservation.reservation;
          const isReturn = contactModalReservation.rowType === 'return';
          const arrivalKey = isReturn ? '返却連絡' : '到着連絡';
          const placeKey = isReturn ? '返却場所' : '空港';
          const options: any[] = isReturn
            ? RETURN_SUB_ZONES.map(sz => ({ id: sz.id, label: sz.label, value: sz.value, borderColor: sz.borderColor, textColor: (sz as any).textColor }))
            : PICKUP_SUB_ZONES.filter(sz => sz.airportValue !== '未')
              .map(sz => ({ id: sz.id, label: sz.label, value: sz.airportValue, borderColor: sz.borderColor, textColor: (sz as any).textColor }));
          const jd = r.json_data || r.detail?.json_data;
          const hasArrival = jd?.data?.[arrivalKey] === '有り';
          const currentPlace = jd?.data?.[placeKey];

          // 予約の json_data.data を差分更新して保存する
          const saveContact = async (mutate: (data: any) => void, message: string) => {
            try {
              const currentJsonData = jd || { data: {} };
              const newData = { ...(currentJsonData.data || {}) };
              mutate(newData);
              dropStaleAltShuttleKey(newData); // 空港を送迎車以外にしたら別車両番号も消す
              const saved = await saveDispatchReservation({
                ...r,
                json_data: { ...currentJsonData, data: newData },
                detail: { ...(r.detail || {}), json_data: { ...currentJsonData, data: newData } },
              });
              setToast({ open: true, message, severity: 'success' });
              setContactModalReservation(null);
              // 貸出の「連絡」が 送迎車/返却車 になったら送迎の運転者を聞く（2026-09-19）。
              // 車両ポジションのサブゾーンへの DnD・店舗「出発準備」と同じ
              if (!isReturn && needsShuttleDriver(getArrivalContact({ json_data: { data: newData } }))) {
                setTimeout(() => handleStaffClick(saved || r, 'driver', 'departure'), 300);
              }
            } catch {
              setToast({ open: true, message: '更新に失敗しました', severity: 'error' });
            }
          };

          return (
            <>
              <DialogTitle sx={{ pb: 1, fontSize: '1rem' }}>{r.customer_name}</DialogTitle>
              <DialogContent sx={{ pb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {isReturn
                    ? `${dayjs(r.end_date).format('HH:mm')} 返却`
                    : `${dayjs(r.start_date).format('HH:mm')} 貸出`}
                </Typography>
                <Button
                  variant={hasArrival ? 'contained' : 'outlined'}
                  color={hasArrival ? 'success' : 'inherit'}
                  fullWidth
                  onClick={() => saveContact(
                    (data) => {
                      if (hasArrival) {
                        delete data[arrivalKey];
                        delete data[placeKey];
                      } else {
                        data[arrivalKey] = '有り';
                        // 「連絡済」（こちらから電話した印）からの到着連絡は「未」から始める（2026-10-19）
                        if (!data[placeKey] || data[placeKey] === AIRPORT_CALLED) data[placeKey] = '未';
                      }
                    },
                    hasArrival ? `${arrivalKey}を取消` : `${arrivalKey}を記録`,
                  )}
                  sx={{ mb: 1 }}
                >
                  {hasArrival ? `${arrivalKey}: ON（クリックで取消）` : arrivalKey}
                </Button>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {options.map(opt => {
                    const isActive = currentPlace === opt.value;
                    return (
                      <Button
                        key={opt.id}
                        variant={isActive ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => saveContact(
                          (data) => {
                            data[arrivalKey] = '有り';
                            data[placeKey] = isActive ? '未' : opt.value;
                          },
                          `${r.customer_name} → ${isActive ? '未設定' : opt.label}`,
                        )}
                        sx={{
                          fontSize: '0.75rem',
                          ...(isActive
                            ? { backgroundColor: opt.borderColor, color: opt.textColor || '#fff', '&:hover': { backgroundColor: opt.borderColor, filter: 'brightness(0.9)' } }
                            : { borderColor: opt.borderColor, color: opt.borderColor }
                          ),
                        }}
                      >
                        {opt.label}
                      </Button>
                    );
                  })}
                  {(hasArrival || (!isReturn && currentPlace === AIRPORT_CALLED)) && (
                    <Button
                      variant="outlined"
                      size="small"
                      color="inherit"
                      onClick={() => saveContact(
                        (data) => { delete data[arrivalKey]; delete data[placeKey]; },
                        `${r.customer_name} → クリア`,
                      )}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      クリア
                    </Button>
                  )}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setContactModalReservation(null)}>閉じる</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* スタッフ選択モーダル */}
      <Dialog
        open={staffModal.open}
        onClose={() => setStaffModal({ ...staffModal, open: false })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {staffModal.staffType === 'driver' ? '運転者' : '対応者'}を選択
          {staffModal.reservation && (
            <Typography variant="body2" color="text.secondary">
              {staffModal.reservation.customer_name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid {...{container: true} as any} spacing={1}>
            {/* 未指定オプション */}
            <Grid {...{item: true, xs: 4} as any}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleStaffSelect(null)}
                sx={{
                  height: '100%',
                  borderColor: '#bdbdbd',
                  color: '#757575',
                  '&:hover': { backgroundColor: '#f5f5f5' }
                }}
              >
                未指定
              </Button>
            </Grid>
            {/* スタッフリスト */}
            {getFilteredStaffList(getCurrentStaffId()).map((staff) => {
              const isCurrentStaff = staff.id === getCurrentStaffId();
              return (
                <Grid {...{item: true, xs: 4} as any} key={staff.id}>
                  <Button
                    fullWidth
                    variant={staff.bg_color ? "contained" : "outlined"}
                    onClick={() => handleStaffSelect(staff)}
                    sx={{
                      height: '100%',
                      backgroundColor: staff.bg_color || undefined,
                      color: staff.text_color || undefined,
                      borderColor: staff.bg_color ? 'transparent' : (staff.text_color || undefined),
                      boxShadow: isCurrentStaff ? `0 0 0 2px #1976d2` : undefined,
                      '&:hover': {
                        backgroundColor: staff.bg_color ? `${staff.bg_color}dd` : undefined,
                        filter: 'brightness(0.9)',
                      }
                    }}
                  >
                    {staff.staff_name}
                  </Button>
                </Grid>
              );
            })}
          </Grid>
          {/* ピン留めスタッフセクション（運転者のみ） */}
          {staffModal.staffType === 'driver' && pinnedStaffUsers.length > 0 && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, mb: 0.5, display: 'block' }}>
                スポットスタッフ
              </Typography>
              <Grid {...{container: true} as any} spacing={1}>
                {pinnedStaffUsers.map((pStaff) => (
                  <Grid {...{item: true, xs: 4} as any} key={pStaff.line_user_id}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => handleStaffSelect({ ...pStaff, _isPinnedStaff: true })}
                      sx={{
                        height: '100%',
                        backgroundColor: '#e8f5e9',
                        borderColor: '#06C755',
                        color: '#2e7d32',
                        '&:hover': {
                          backgroundColor: '#c8e6c9',
                        }
                      }}
                    >
                      {pStaff.staffName}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStaffModal({ ...staffModal, open: false })}>
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDialog.open}
        onClose={() => handleConfirmClose(false)}
      >
        <DialogTitle>確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            チェックを初期化しますか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleConfirmClose(false)} color="primary">
            いいえ (そのまま)
          </Button>
          <Button onClick={() => handleConfirmClose(true)} color="primary" autoFocus>
            はい (初期化)
          </Button>
        </DialogActions>
      </Dialog>

      {/* 車両アサインモーダル */}
      <VehicleAssignModal
        open={assignModalOpen}
        onClose={handleCloseAssignModal}
        reservation={assignTargetReservation}
        vehicleList={Object.values(vehicles)}
        vehicles={vehicles}
        carTypesList={carTypesList}
        onAssign={executeAssignment}
        onUpdateVehicleStatus={handleUpdateVehicleStatus}
        vehicleStatusList={vehicleStatusList}
        allReservations={[...departures, ...returns]}
      />


      {/* Square 支払確認モーダル */}
      <Dialog
        open={squareModal.open}
        onClose={() => setSquareModal({ open: false, reservation: null, paymentData: null, loading: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6" component="span">支払確認</Typography>
            {squareModal.reservation && (
              <Typography variant="subtitle2" color="text.secondary">
                {squareModal.reservation.customer_name} ({squareModal.reservation.contact_code || squareModal.reservation.detail?.contact_id})
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* 支払状態の手動変更ボタン（保存処理は handleSetPaymentStatus に共通化。2026-07-26） */}
            {(() => {
              const currentPayment = Number(squareModal.reservation?.detail?.payment ?? 0);
              const isPaid = currentPayment === 1;
              const squarePaid = isSquarePaid(squareModal.paymentData);

              return (
                <Button
                  variant={isPaid ? 'outlined' : 'contained'}
                  color={isPaid ? 'warning' : 'success'}
                  size="small"
                  onClick={() => {
                    const reservation = squareModal.reservation;
                    if (!reservation) return;
                    const newPaymentStatus = isPaid ? 0 : 1;
                    // Square 未払いのまま手動で確認済みにする場合は注意トーストを出す
                    handleSetPaymentStatus(reservation, newPaymentStatus, {
                      squareUnpaid: newPaymentStatus === 1 && !squarePaid,
                    });
                    // モーダルは閉じない（続けて確認できるように）
                  }}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {isPaid ? '未払に戻す' : '確認済みにする'}
                </Button>
              );
            })()}
            <IconButton
              onClick={() => setSquareModal({ open: false, reservation: null, paymentData: null, loading: false })}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ minHeight: '150px' }}>
          {/* 支払い済みフラグONだが Square 未払いの場合の注意表示（2026-07-26） */}
          {!squareModal.loading
            && Number(squareModal.reservation?.detail?.payment ?? 0) === 1
            && !isSquarePaid(squareModal.paymentData) && (
            <Box sx={{ mb: 1.5, px: 1.2, py: 0.7, bgcolor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 1 }}>
              <Typography sx={{ fontSize: 12.5, color: '#b91c1c', fontWeight: 700 }}>
                ⚠ Squareでは支払い済みになっていません（手動確認のみの状態です）
              </Typography>
            </Box>
          )}
          {squareModal.loading ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress size={30} sx={{ mb: 2 }} />
              <Typography color="text.secondary">Squareデータを読み込み中...</Typography>
            </Box>
          ) : !squareModal.paymentData || squareModal.paymentData.invoices?.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                該当するSquare請求データがありません。
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#7c3aed' }}>
                Square決済DB ({squareModal.paymentData.invoices?.length || 0}件)
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f3e8ff' }}>
                      <TableCell sx={{ fontWeight: 600 }}>請求番号</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>ステータス</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>金額</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>支払日</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>作成日</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>商品</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {squareModal.paymentData.invoices.map((inv: any, idx: number) => {
                      const order = squareModal.paymentData.orders?.find((o: any) => o.order_id === inv.order_id);
                      const lineItems = order?.line_items || [];
                      const payment = squareModal.paymentData.payments?.find((p: any) => p.order_id === inv.order_id);
                      // 返金系（REFUNDED / PARTIALLY_REFUNDED）を含む共通マッピングを使う
                      const chip = invoiceStatusChip(inv.status);
                      return (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {inv.invoice_number || '-'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={chip.label}
                              title={inv.status}
                              size="small"
                              sx={{ bgcolor: chip.bg, color: chip.color, fontSize: '0.7rem', fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            ¥{((inv.computed_amount || 0) / 1).toLocaleString()}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>
                            {payment?.created_at ? dayjs(payment.created_at).format('MM/DD HH:mm') : inv.updated_at ? dayjs(inv.updated_at).format('MM/DD HH:mm') : '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>
                            {inv.created_at ? dayjs(inv.created_at).format('YYYY/MM/DD') : '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', maxWidth: 200 }}>
                            {lineItems.length > 0
                              ? lineItems.map((li: any, i: number) => (
                                  <div key={i}>{li.name} x{li.quantity}</div>
                                ))
                              : '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {/* 決済額 / 返金額 / 実質支払額。旅程変更などで一部返金すると
                  請求書の金額と実際の入金額がズレるため、内訳を明示する（2026-08-05） */}
              {(() => {
                const amounts = computeSquareAmounts(squareModal.paymentData);
                if (!amounts.hasRefunds) return null;
                const refundCount = squareModal.paymentData.refunds?.length || 0;
                return (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    決済 ¥{amounts.paidAmount.toLocaleString()} / 返金 -¥
                    {amounts.refundAmount.toLocaleString()}
                    {refundCount > 0 ? `（${refundCount}件）` : ''} → <b>実質 ¥
                    {amounts.netAmount.toLocaleString()}</b>
                  </Alert>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSquareModal({ open: false, reservation: null, paymentData: null, loading: false })}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* LINE Chat Modal */}
      <Dialog
        open={lineChatModal.open}
        onClose={handleCloseLineChat}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            height: isMobile ? '100%' : '85vh',
            borderRadius: isMobile ? 0 : '16px',
            overflow: 'visible',
            position: 'relative'
          }
        }}
      >
        <IconButton
          onClick={handleCloseLineChat}
          sx={{
            position: 'absolute',
            right: isMobile ? 8 : -48,
            top: isMobile ? 8 : 0,
            zIndex: 2000,
            color: isMobile ? '#64748b' : 'white',
            bgcolor: isMobile ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.3)',
            '&:hover': {
              bgcolor: isMobile ? 'white' : 'rgba(0,0,0,0.5)',
              color: isMobile ? '#000' : 'white'
            },
            boxShadow: isMobile ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
          {lineChatModal.open && lineChatModal.lineUser && (
            <LineChatPanel
              user={{
                ...lineChatModal.lineUser,
                instance_reservation_id: lineChatModal.reservationId,
              }}
              onClose={handleCloseLineChat}
              onReload={fetchLineUsers}
              // 既読にする（2026-08-25）。LINE ボードの「既読モード」スイッチは
              // 一覧を眺めるだけで既読が消えるのを避けるための機能で、既定 OFF。
              // daylist は予約行の 💬 から特定のお客様のやりとりを開く＝意図的に読む
              // 操作なので、ここは常に既読にする（スイッチはこの画面から見えない）。
              // markRead は useLineChat の共有ストアも更新するので、行の未読バッジは即座に消える。
              readMode
            />
          )}
        </DialogContent>
      </Dialog>

      {/* 自社チャット（予約チャット）Modal — LINE と同じ開き方（2026-10-30） */}
      <Dialog
        open={Boolean(resvChatSessionKey)}
        onClose={handleCloseResvChat}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { height: isMobile ? '100%' : '85vh', borderRadius: isMobile ? 0 : '16px', overflow: 'visible', position: 'relative' } }}
      >
        <IconButton
          onClick={handleCloseResvChat}
          aria-label="閉じる"
          sx={{
            position: 'absolute', right: isMobile ? 8 : -48, top: isMobile ? 8 : 0, zIndex: 2000,
            color: isMobile ? '#64748b' : 'white', bgcolor: isMobile ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.3)',
            '&:hover': { bgcolor: isMobile ? 'white' : 'rgba(0,0,0,0.5)', color: isMobile ? '#000' : 'white' },
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
          {resvChatInstance ? (
            <ReservationChatPanel
              instance={resvChatInstance}
              onClose={handleCloseResvChat}
              onReload={resvChat.fetchSessions}
              // LINE と同じく、予約行から開く＝意図的に読む操作なので既読にする
              readMode
              onOpenSession={(sk) => setResvChatSessionKey(sk)}
            />
          ) : resvChatSessionKey ? (
            <Box sx={{ p: 4, color: '#64748b', textAlign: 'center' }}>自社チャットを読み込めませんでした。LINE ページから開いてください。</Box>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* 車両スワップ確認Dialog */}
      <Dialog open={Boolean(vehicleSwapConfirm)} onClose={() => setVehicleSwapConfirm(null)}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '16px', pb: 1 }}>車両の入替確認</DialogTitle>
        <DialogContent>
          {vehicleSwapConfirm && (() => {
            const srcVehicle = Object.values(vehicles).find((v: any) => String(v.id) === String(vehicleSwapConfirm.source.vehicle_id)) as any;
            const tgtVehicle = Object.values(vehicles).find((v: any) => String(v.id) === String(vehicleSwapConfirm.target.vehicle_id)) as any;
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, background: '#f1f5f9', textAlign: 'center' }}>
                    <Typography variant="body2" fontWeight={700}>{vehicleSwapConfirm.source.customer_name}</Typography>
                    <Typography variant="h6" fontWeight={800} color="primary">{srcVehicle?.vehicle_code || '?'}</Typography>
                  </Box>
                  <Typography variant="h5" sx={{ color: '#94a3b8' }}>⇄</Typography>
                  <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, background: '#f1f5f9', textAlign: 'center' }}>
                    <Typography variant="body2" fontWeight={700}>{vehicleSwapConfirm.target.customer_name}</Typography>
                    <Typography variant="h6" fontWeight={800} color="primary">{tgtVehicle?.vehicle_code || '?'}</Typography>
                  </Box>
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVehicleSwapConfirm(null)}>キャンセル</Button>
          <Button onClick={executeVehicleSwap} variant="contained" color="primary">入替する</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={6000} onClose={handleCloseToast}>
        <Alert onClose={handleCloseToast} severity={toast.severity as any} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
      {/* 統合表示ではペア連結で表現するため、hover接続ラインは出さない */}
      {!isMobile && !mergedView && <TurnaroundLines hoveredVehicleId={hoveredVehicleId} />}
    </Box>
  );
}

// ターンアラウンド（同日返却・貸出）のドットを結ぶラインを描画するコンポーネント
const TurnaroundLines = ({ hoveredVehicleId }) => {
  const [lines, setLines] = React.useState([]);

  React.useEffect(() => {
    let requestRef;

    const updateLines = () => {
      if (requestRef) cancelAnimationFrame(requestRef);

      requestRef = requestAnimationFrame(() => {
        const dots = document.querySelectorAll('.turnaround-dot');
        const map = {};
        dots.forEach(dot => {
          const vid = dot.getAttribute('data-vehicle-id');
          const type = dot.getAttribute('data-type');
          if (!vid || !type) return;

          const dotRect = dot.getBoundingClientRect();

          // 所属するテーブルコンテナとそのヘッダー内の実際のセルを取得
          const container = dot.closest('.MuiTableContainer-root');
          // stickyHeaderの場合、thが固定の境界線となるためthを取得
          const headerCell = container ? container.querySelector('th') : null;

          let isVisible = true;
          let currentContainerRect = null;
          if (container && headerCell) {
            currentContainerRect = container.getBoundingClientRect();
            const headerRect = headerCell.getBoundingClientRect();

            // ヘッダーセルの下端より上（潜った）、またはコンテナの下端より下にある場合は非表示
            if (dotRect.top < headerRect.bottom || dotRect.bottom > currentContainerRect.bottom) {
              isVisible = false;
            }
          }

          if (!map[vid]) map[vid] = {};
          map[vid][type] = {
            rect: dotRect,
            visible: isVisible,
            containerRect: currentContainerRect
          };
        });

        const newLines = [];
        Object.keys(map).forEach(vid => {
          const dep = map[vid].departure;
          const ret = map[vid].return;
          // 両方のドットが画面内にあり、かつテーブルの可視範囲内にある場合のみ描画
          // かつ、その車両がホバーされている場合のみ
          if (dep && ret && dep.visible && ret.visible && String(vid) === String(hoveredVehicleId)) {
            newLines.push({
              id: vid,
              dep: dep.rect,
              ret: ret.rect,
              depContainerRect: dep.containerRect,
              retContainerRect: ret.containerRect
            });
          }
        });
        setLines(newLines);
      });
    };

    updateLines();
    window.addEventListener('scroll', updateLines, true);
    window.addEventListener('resize', updateLines);

    // アニメーションやロード後のレイアウト変更に対応するため一定間隔で更新
    const timer = setInterval(updateLines, 1000);

    return () => {
      if (requestRef) cancelAnimationFrame(requestRef);
      window.removeEventListener('scroll', updateLines, true);
      window.removeEventListener('resize', updateLines);
      clearInterval(timer);
    };
  }, [hoveredVehicleId]);

  return (
    <svg style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: 9999
    }}>
      {lines.map(line => {
        // 貸出側は横スクロールによるズレを防ぐため、常にテーブルコンテナの右端をX座標とする
        const depX = line.depContainerRect ? line.depContainerRect.right : line.dep.left + line.dep.width / 2;
        const depY = line.dep.top + line.dep.height / 2;
        const retX = line.ret.left + line.ret.width / 2;
        const retY = line.ret.top + line.ret.height / 2;

        return (
          <React.Fragment key={line.id}>
            {/* 接続ライン */}
            <line
              x1={depX}
              y1={depY}
              x2={retX}
              y2={retY}
              stroke="#d32f2f"
              strokeWidth="1.5"
              strokeOpacity="0.4"
              strokeDasharray="4 2"
            />
          </React.Fragment>
        );
      })}
    </svg>
  );
};
